import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, API } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import TimeOffCalendar from "@/components/TimeOffCalendar";
import { DollarSign, TrendingUp, ClipboardList, Trash2, Check, X, UserX, Award, ShieldCheck, Plus, Pencil, Phone, MessageSquare, KeyRound, AlertTriangle, Ban, RotateCcw, FileText, Gift, Wrench, HeartHandshake, Sparkles, Mail } from "lucide-react";

const RED = "#9b111e";

const Pager = ({ pg, onPage, testid }) => pg.pages > 1 ? (
  <div className="flex items-center justify-between gap-2 mt-3" data-testid={testid}>
    <button disabled={pg.page <= 1} onClick={() => onPage(pg.page - 1)} data-testid={`${testid}-prev`}
      className="text-xs font-bold border border-slate-300 rounded-lg px-3 py-1.5 disabled:opacity-40 hover:border-[#9b111e]">← Prev</button>
    <span className="text-xs text-slate-500">Page {pg.page} of {pg.pages} · {pg.total} total</span>
    <button disabled={pg.page >= pg.pages} onClick={() => onPage(pg.page + 1)} data-testid={`${testid}-next`}
      className="text-xs font-bold border border-slate-300 rounded-lg px-3 py-1.5 disabled:opacity-40 hover:border-[#9b111e]">Next →</button>
  </div>
) : null;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookingsPg, setBookingsPg] = useState({ page: 1, pages: 1, total: 0 });
  const [bookingSearch, setBookingSearch] = useState("");
  const [usersPg, setUsersPg] = useState({ page: 1, pages: 1, total: 0, clientCount: 0 });
  const [userSearch, setUserSearch] = useState("");
  const [proSearch, setProSearch] = useState("");
  const [reviews, setReviews] = useState([]);
  const [links, setLinks] = useState([]);
  const [apps, setApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [removeEmail, setRemoveEmail] = useState("");
  const [pros, setPros] = useState([]);
  const [certInput, setCertInput] = useState({});
  const [idViewer, setIdViewer] = useState(null);
  const [milVerifs, setMilVerifs] = useState([]);
  const [milViewer, setMilViewer] = useState(null);
  const [timeOffReqs, setTimeOffReqs] = useState([]);
  const [interviewApp, setInterviewApp] = useState(null);
  const [interviewSlots, setInterviewSlots] = useState(["", ""]);

  const fmtSlot = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
  };
  const openInterview = (app) => { setInterviewApp(app); setInterviewSlots(["", ""]); };
  const submitInterview = async () => {
    const slots = interviewSlots.filter((s) => s);
    if (slots.length === 0) return toast.error("Add at least one time slot");
    try {
      await api.post(`/admin/applications/${interviewApp.id}/request-interview`, { slots });
      toast.success("Interview times sent — the pro will pick one");
      setInterviewApp(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const decideTimeOff = async (id, approve) => {
    try {
      await api.post(`/admin/time-off-requests/${id}/decision`, { approve });
      toast.success(approve ? "Time off approved & blocked on the pro's calendar" : "Request rejected");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const viewDoctorNote = async (id) => {
    try {
      const res = await api.get(`/admin/time-off-requests/${id}/doctor-note`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("No doctor's note on file"); }
  };
  const releaseFromRequest = async (r) => {
    if (!window.confirm(`Release ${r.pro_name}? This permanently removes their account.`)) return;
    try {
      await api.post("/admin/users/remove", { email: r.pro_email });
      toast.success(`${r.pro_name} released`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [stripeKey, setStripeKey] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [hireDoc, setHireDoc] = useState(null);
  const [uploadingHireDoc, setUploadingHireDoc] = useState(false);
  const [acks, setAcks] = useState(null);
  const [nudgingPro, setNudgingPro] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  const nudgePro = async (p) => {
    setNudgingPro(p.id);
    try {
      await api.post(`/admin/standards-nudge/${p.id}`);
      toast.success(`Reminder sent to ${p.name} by email & text`);
      api.get("/admin/standards-acks").then(({ data }) => setAcks(data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setNudgingPro(null); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/send-digest");
      toast.success(`Weekly digest emailed to ${data.sent_to} admin(s)`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSendingDigest(false); }
  };
  const [offerCands, setOfferCands] = useState(null);
  const [sendingOffer, setSendingOffer] = useState(null);
  const [qcData, setQcData] = useState(null);
  const [qcNoteBusy, setQcNoteBusy] = useState(null);
  const [qcEditNotes, setQcEditNotes] = useState({});
  const [flaggedMsgs, setFlaggedMsgs] = useState([]);
  const [msgThread, setMsgThread] = useState(null);
  const [editMsgId, setEditMsgId] = useState(null);
  const [editMsgText, setEditMsgText] = useState("");

  const loadFlaggedMsgs = () => api.get("/admin/messages/flagged").then(({ data }) => setFlaggedMsgs(data)).catch(() => {});
  const openMsgThread = async (m) => {
    try {
      const { data } = await api.get("/admin/messages/thread", { params: { a: m.sender_id, b: m.recipient_id } });
      setMsgThread({ flagged: m, msgs: data });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveMsgEdit = async (id) => {
    try {
      await api.put(`/admin/messages/${id}`, { text: editMsgText });
      toast.success("Message edited — both users now see the updated text");
      setEditMsgId(null);
      loadFlaggedMsgs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delMsg = async (id) => {
    if (!window.confirm("Remove this message for both users?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      toast.success("Message removed from the conversation");
      loadFlaggedMsgs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const dismissMsgFlag = async (id) => {
    try {
      await api.post(`/admin/messages/${id}/dismiss`);
      toast.success("Flag dismissed — message left as-is");
      loadFlaggedMsgs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const suspendSender = async (m) => {
    const suspend = !m.sender_suspended;
    if (suspend && !window.confirm(`Suspend ${m.sender_name}? They'll be blocked from ${m.sender_role === "professional" ? "search, bookings" : "booking"} and messaging until reinstated.`)) return;
    try {
      await api.post(`/admin/messages/${m.id}/suspend-sender`, { suspend });
      toast.success(suspend ? `${m.sender_name} suspended — they've been notified by email & text` : `${m.sender_name} reinstated`);
      loadFlaggedMsgs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const decideQcNote = async (checkId, decision) => {
    setQcNoteBusy(checkId);
    try {
      await api.post(`/admin/qc-checks/${checkId}/note/${decision}`,
        decision === "approve" ? { edited_note: qcEditNotes[checkId] || null } : {});
      toast.success(decision === "approve" ? "Comment approved & delivered to the pro" : "Comment rejected — it will not be sent");
      setQcEditNotes((n) => { const c = { ...n }; delete c[checkId]; return c; });
      api.get("/admin/qc-checks").then(({ data }) => setQcData(data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setQcNoteBusy(null); }
  };

  const sendReviewOffer = async (c) => {
    setSendingOffer(c.id);
    try {
      await api.post(`/admin/review-offer/${c.id}`);
      toast.success(`10% offer sent to ${c.name} by email & text`);
      api.get("/admin/review-offer-candidates").then(({ data }) => setOfferCands(data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSendingOffer(null); }
  };

  const decideQcReply = async (checkId, decision) => {
    setQcNoteBusy(checkId);
    try {
      await api.post(`/admin/qc-checks/${checkId}/reply/${decision}`,
        decision === "approve" ? { edited_note: qcEditNotes[`r-${checkId}`] || null } : {});
      toast.success(decision === "approve" ? "Thank-you approved & delivered to the client" : "Thank-you rejected — it will not be sent");
      setQcEditNotes((n) => { const c = { ...n }; delete c[`r-${checkId}`]; return c; });
      api.get("/admin/qc-checks").then(({ data }) => setQcData(data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setQcNoteBusy(null); }
  };

  const previewHireDoc = async () => {
    try {
      const res = await api.get("/admin/new-hire-doc/download", { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("No PDF uploaded yet"); }
  };

  const uploadHireDoc = async (file) => {
    if (!file) return;
    setUploadingHireDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/new-hire-doc", fd);
      setHireDoc({ uploaded: true, ...data });
      toast.success("New Hire Welcome PDF uploaded — it now goes out with every approval email");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploadingHireDoc(false); }
  };

  const saveStripeKey = async () => {
    const k = newKey.trim();
    if (!k.startsWith("sk_")) return toast.error("Enter a valid Stripe secret key (starts with 'sk_')");
    setSavingKey(true);
    try {
      const { data } = await api.put("/admin/stripe-key", { api_key: k });
      setStripeKey(data);
      setNewKey("");
      toast.success(`Stripe ${data.mode} key updated securely`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingKey(false); }
  };

  const viewMilitary = async (u) => {
    try {
      const res = await api.get(`/admin/military/${u.id}/id-document`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setMilViewer({ uid: u.id, name: u.name, url });
    } catch { toast.error("No military ID on file for this user"); }
  };

  const verifyMilitary = async (approve) => {
    try {
      await api.post(`/admin/military/${milViewer.uid}/verify`, { approve });
      toast.success(approve ? "Verified Veteran — discount enabled & ID deleted" : "Rejected & military ID deleted");
      if (milViewer.url) URL.revokeObjectURL(milViewer.url);
      setMilViewer(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const viewId = async (p) => {
    try {
      const res = await api.get(`/admin/professionals/${p.id}/id-document`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setIdViewer({ pid: p.id, name: p.name, url });
    } catch { toast.error("No ID on file for this professional"); }
  };

  const deleteId = async () => {
    try {
      await api.delete(`/admin/professionals/${idViewer.pid}/id-document`);
      toast.success("ID permanently deleted");
      if (idViewer.url) URL.revokeObjectURL(idViewer.url);
      setIdViewer(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const loadBookings = (page = 1, q = bookingSearch) =>
    api.get("/admin/bookings", { params: { page, page_size: 50, q: q || undefined } }).then(({ data }) => {
      setBookings(data.items);
      setBookingsPg({ page: data.page, pages: data.pages, total: data.total });
    }).catch(() => {});
  const loadUsers = (page = 1, q = userSearch) =>
    api.get("/admin/users", { params: { page, page_size: 50, q: q || undefined } }).then(({ data }) => {
      setUsers(data.items);
      setUsersPg({ page: data.page, pages: data.pages, total: data.total, clientCount: data.client_count });
    }).catch(() => {});

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    loadBookings(1);
    api.get("/admin/reviews").then(({ data }) => setReviews(data)).catch(() => {});
    api.get("/admin/caregiver-links").then(({ data }) => setLinks(data)).catch(() => {});
    api.get("/admin/applications").then(({ data }) => setApps(data)).catch(() => {});
    loadUsers(1);
    api.get("/admin/professionals").then(({ data }) => setPros(data)).catch(() => {});
    api.get("/admin/military-verifications").then(({ data }) => setMilVerifs(data)).catch(() => {});
    api.get("/admin/time-off-requests").then(({ data }) => setTimeOffReqs(data)).catch(() => {});
    api.get("/admin/stripe-key").then(({ data }) => setStripeKey(data)).catch(() => {});
    api.get("/admin/new-hire-doc").then(({ data }) => setHireDoc(data)).catch(() => {});
    api.get("/admin/standards-acks").then(({ data }) => setAcks(data)).catch(() => {});
    api.get("/admin/review-offer-candidates").then(({ data }) => setOfferCands(data)).catch(() => {});
    api.get("/admin/qc-checks").then(({ data }) => setQcData(data)).catch(() => {});
    api.get("/admin/review-flags").then(({ data }) => setFlags(data)).catch(() => {});
    api.get("/admin/home-reports").then(({ data }) => setHomeReports(data)).catch(() => {});
    api.get("/admin/clean-reports").then(({ data }) => setCleanReports(data)).catch(() => {});
    api.get("/admin/referral-leaderboard").then(({ data }) => setRefLeaders(data.leaders || [])).catch(() => {});
    api.get("/admin/messages/flagged").then(({ data }) => setFlaggedMsgs(data)).catch(() => {});
  };
  useEffect(load, []);

  const toggleCert = async (p) => {
    try {
      await api.put(`/admin/professionals/${p.id}/certification`, { approved: !p.approved });
      toast.success(!p.approved ? `${p.name} certified` : `${p.name} un-certified`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleSuspend = async (p) => {
    try {
      await api.put(`/admin/professionals/${p.id}/suspend`, { suspended: !p.suspended });
      toast.success(!p.suspended ? `${p.name} suspended — hidden from search` : `${p.name} reinstated`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [coachPro, setCoachPro] = useState(null);
  const [coachSlots, setCoachSlots] = useState(["", ""]);
  const [flags, setFlags] = useState([]);
  const [homeReports, setHomeReports] = useState([]);
  const [cleanReports, setCleanReports] = useState([]);
  const loadHomeReports = () => {
    api.get("/admin/home-reports").then(({ data }) => setHomeReports(data)).catch(() => {});
    api.get("/admin/clean-reports").then(({ data }) => setCleanReports(data)).catch(() => {});
  };
  const viewCleanPhoto = async (bid, idx) => {
    try {
      const res = await api.get(`/admin/clean-reports/${bid}/photo/${idx}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not load photo"); }
  };
  const [scoreUser, setScoreUser] = useState(null);
  const [scoreForm, setScoreForm] = useState({ score: "10", clear: true });
  const terminateClient = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/terminate`);
      toast.success(`${u.name} terminated permanently — ${data.bookings_cancelled} upcoming booking(s) cancelled`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const reinstateClient = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/reinstate`);
      toast.success(`${u.name} reinstated — final-chance email sent`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [refLeaders, setRefLeaders] = useState([]);
  const [nudgeUser, setNudgeUser] = useState(null);
  const [nudgePreview, setNudgePreview] = useState(null);
  const openNudge = async (u) => {
    setNudgeUser(u);
    setNudgePreview(null);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/cleaning-reminder-preview`);
      setNudgePreview(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setNudgeUser(null);
    }
  };
  const sendNudge = async () => {
    try {
      const { data } = await api.post(`/admin/users/${nudgeUser.id}/cleaning-reminder`);
      toast.success(`Reminder sent to ${data.to}`);
      setNudgeUser(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submitHomeScore = async () => {
    const s = parseInt(scoreForm.score);
    if (!(s >= 1 && s <= 10)) return toast.error("Home score must be between 1 and 10");
    try {
      await api.post(`/admin/users/${scoreUser.id}/home-score`, { score: s, clear_hazard: scoreForm.clear });
      toast.success(scoreForm.clear ? "Home score reset — hazard cleared & coaching email sent" : "Home score updated");
      setScoreUser(null);
      load();
      loadHomeReports();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resolveHomeReport = async (bid) => {
    try {
      await api.post(`/admin/bookings/${bid}/home-report/resolve`);
      toast.success("Marked as reviewed");
      loadHomeReports();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [flagCall, setFlagCall] = useState(null);
  const [flagCallSlots, setFlagCallSlots] = useState(["", ""]);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveForm, setResolveForm] = useState({ resolution: "none", notes: "", credit_amount: "" });
  const [creditUser, setCreditUser] = useState(null);
  const [creditForm, setCreditForm] = useState({ amount: "", reason: "" });
  const [selectedClients, setSelectedClients] = useState([]);
  const [bulkForm, setBulkForm] = useState({ amount: "", reason: "" });
  const toggleClient = (id) => setSelectedClients((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const togglePause = async (u) => {
    const next = !u.paused;
    if (next && !window.confirm(`Pause ${u.name}? They won't be able to book new visits until you resume them.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/pause`, { paused: next });
      toast.success(next ? `${u.name} is paused from booking` : `${u.name} can book again`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const clearCleaning = async (u) => {
    if (!window.confirm(`Clear the mandatory cleaning requirement for ${u.name}? They'll be able to book any service again without completing a Care-4U cleaning.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/clear-cleaning`);
      toast.success(`${u.name} can book any service again`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submitBulk = async (allClients) => {
    const amt = parseFloat(bulkForm.amount);
    if (!amt || amt <= 0) return toast.error("Enter a credit amount greater than $0");
    if (!allClients && selectedClients.length === 0) return toast.error("Select at least one client (or use Grant to ALL)");
    const clientCount = usersPg.clientCount;
    if (allClients && !window.confirm(`Grant $${amt.toFixed(2)} to ALL ${clientCount} clients? This can't be undone.`)) return;
    try {
      const { data } = await api.post("/admin/credit/bulk", {
        amount: amt, reason: bulkForm.reason,
        all_clients: !!allClients, user_ids: allClients ? [] : selectedClients,
      });
      toast.success(`Granted $${amt.toFixed(2)} to ${data.count} client${data.count === 1 ? "" : "s"} ($${data.total.toFixed(2)} total)`);
      setBulkForm({ amount: "", reason: "" });
      setSelectedClients([]);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submitGrantCredit = async () => {
    const amt = parseFloat(creditForm.amount);
    if (!amt || amt <= 0) return toast.error("Enter a credit amount greater than $0");
    try {
      const { data } = await api.post(`/admin/users/${creditUser.id}/grant-credit`, { amount: amt, reason: creditForm.reason });
      toast.success(`Granted $${data.amount.toFixed(2)} — new balance $${data.balance.toFixed(2)}`);
      setCreditUser(null);
      setCreditForm({ amount: "", reason: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const loadFlags = () => {
    api.get("/admin/review-flags").then(({ data }) => setFlags(data)).catch(() => {});
  };
  const openFlagCall = (r) => { setFlagCall(r); setFlagCallSlots(["", ""]); };
  const sendRedo = async (reviewId) => {
    if (!window.confirm("Create a FREE re-do booking for this client with the same pro? The client picks a time. The pro is not paid for a re-do (they're correcting their own work).")) return;
    try {
      await api.post(`/admin/reviews/${reviewId}/redo`);
      toast.success("Free re-do created — the client will pick a time");
      loadFlags();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submitFlagCall = async () => {
    const slots = flagCallSlots.filter((s) => s);
    if (slots.length === 0) return toast.error("Add at least one time slot");
    try {
      await api.post(`/admin/reviews/${flagCall.id}/flag-call`, { slots });
      toast.success("Call times sent to the client");
      setFlagCall(null);
      loadFlags();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submitResolve = async (decision) => {
    try {
      await api.post(`/admin/reviews/${resolveTarget.id}/resolve`, {
        decision, resolution: resolveForm.resolution, notes: resolveForm.notes,
        credit_amount: resolveForm.resolution === "credit" ? parseFloat(resolveForm.credit_amount || 0) : 0,
      });
      toast.success(decision === "remove" ? "Review removed & pro's strike reversed" : "Review kept & restored");
      setResolveTarget(null);
      setResolveForm({ resolution: "none", notes: "", credit_amount: "" });
      loadFlags();
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const reinstatePro = async (p) => {
    if (!window.confirm(`Reinstate ${p.name} to FULL Pro status? This clears their strikes.`)) return;
    try {
      await api.post(`/admin/professionals/${p.id}/reinstate`);
      toast.success(`${p.name} reinstated to full status & notified`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openCoaching = (p) => { setCoachPro(p); setCoachSlots(["", ""]); };
  const submitCoaching = async () => {
    const slots = coachSlots.filter((s) => s);
    if (slots.length === 0) return toast.error("Add at least one time slot");
    try {
      await api.post(`/admin/professionals/${coachPro.id}/coaching`, { slots });
      toast.success("Coaching times sent — the pro will pick one");
      setCoachPro(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const releasePro = async (p) => {
    if (!window.confirm(`Permanently remove ${p.name} and ALL their data? This cannot be undone.`)) return;
    try {
      await api.post("/admin/users/remove", { email: p.email });
      toast.success(`${p.name} permanently removed`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addCert = async (pid) => {
    const name = (certInput[pid] || "").trim();
    if (!name) return toast.error("Enter a certificate name");
    try {
      await api.post(`/admin/professionals/${pid}/certificates`, { name });
      setCertInput({ ...certInput, [pid]: "" });
      toast.success("Certificate added");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeCert = async (pid, cid) => {
    try {
      await api.delete(`/admin/professionals/${pid}/certificates/${cid}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const renameUser = async (u) => {
    const name = window.prompt(`New name for ${u.email}:`, u.name);
    if (!name || name.trim() === u.name) return;
    try {
      await api.put(`/admin/users/${u.id}/name`, { name: name.trim() });
      toast.success("Name updated");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeUserByEmail = async (email) => {
    if (!email) return toast.error("Enter an email");
    if (!window.confirm(`Permanently remove ${email} and all their data? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/users/remove", { email });
      toast.success(`Removed ${data.name || data.email} (${data.role})`);
      setRemoveEmail("");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sendFeedbackEmail = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/feedback-email`);
      toast.success(`Feedback request emailed to ${u.name || u.email}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const decide = async (id, decision) => {
    try {
      await api.post(`/admin/applications/${id}/${decision}`);
      const msg = decision === "approve" ? "Applicant approved & notified"
        : decision === "reject" ? "Applicant declined & notified"
        : "Phone interview requested — applicant notified";
      toast.success(msg);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const viewCert = async (pid, certId) => {
    try {
      const res = await api.get(`/admin/professionals/${pid}/certifications/${certId}/document`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("No document on file for this certification"); }
  };
  const deleteCert = async (pid, certId) => {
    if (!window.confirm("Permanently delete this certificate document after verifying it? The name stays on the application.")) return;
    try {
      await api.delete(`/admin/professionals/${pid}/certifications/${certId}/document`);
      toast.success("Certificate verified & permanently deleted");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const verifyCert = async (pid, certId) => {
    try {
      await api.post(`/admin/professionals/${pid}/certifications/${certId}/verify`);
      toast.success("Certification approved — now shows as a public badge");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const viewResume = async (pid) => {
    try {
      const res = await api.get(`/admin/professionals/${pid}/resume`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("No resume on file for this applicant"); }
  };
  const viewExpDoc = async (pid, docId) => {
    try {
      const res = await api.get(`/admin/professionals/${pid}/experience-docs/${docId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open this document"); }
  };

  const delReview = async (id) => {
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Review removed");
    load();
  };

  const togglePhoto = async (rid, idx) => {
    try {
      const { data } = await api.post(`/admin/reviews/${rid}/photos/${idx}/toggle-hide`);
      toast.success(data.status === "hidden" ? "Photo hidden from the public gallery" : "Photo restored to the gallery");
      api.get("/admin/reviews").then(({ data: d }) => setReviews(d)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const previewHiddenPhoto = async (rid, idx) => {
    try {
      const res = await api.get(`/admin/reviews/${rid}/photo/${idx}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Couldn't load that photo"); }
  };

  const confirmCash = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/confirm-cash`);
      toast.success("Cash App payment confirmed — booking marked paid");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const delLink = async (id) => {
    try {
      await api.delete(`/admin/caregiver-links/${id}`);
      toast.success("Caregiver/client link removed");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const catData = stats ? Object.entries(stats.by_category).map(([k, v]) => ({ name: k, sales: v.sales, fees: v.fees })) : [];
  const cityData = stats ? Object.entries(stats.by_city).map(([k, v]) => ({ name: k, sales: v.sales })) : [];

  const Stat = ({ icon: Icon, label, value, testId }) => (
    <div className="card-metal p-5">
      <Icon className="w-6 h-6 text-[#9b111e] mb-2" />
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-heading font-black text-2xl text-slate-900" data-testid={testId}>{value}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {stripeKey?.needs_rotation && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="stripe-rotation-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-bold text-amber-800">Time to rotate your Stripe key 🔐</p>
            <p className="text-amber-700">
              Your Stripe secret key is {stripeKey.days_since_update} days old. For security, generate a fresh key in
              your Stripe Dashboard and paste it under <b>Settings</b> at least every {stripeKey.rotation_days} days.
            </p>
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={DollarSign} label="Total Sales" value={`$${stats?.total_sales ?? 0}`} testId="stat-sales" />
        <Stat icon={TrendingUp} label="Platform Fees (5%)" value={`$${stats?.total_fees ?? 0}`} testId="stat-fees" />
        <Stat icon={ClipboardList} label="Paid Bookings" value={stats?.booking_count ?? 0} testId="stat-bookings" />
        <div className="card-metal p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500">Payout Account</p>
          <p className="font-heading font-black text-sm text-slate-900 break-all" data-testid="stat-payout">{stats?.payout_account || "—"}</p>
          <p className="text-xs text-slate-400 mt-1">Stripe direct deposit</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" data-testid="goodwill-stats">
        <Stat icon={Gift} label="Credits Granted (this month)" value={`$${stats?.credits_granted_month ?? 0}`} testId="stat-credits-month" />
        <Stat icon={Wrench} label="Free Re-Do Value (this month)" value={`$${stats?.redo_value_month ?? 0}`} testId="stat-redo-month" />
        <Stat icon={HeartHandshake} label="Total Goodwill (this month)" value={`$${stats?.goodwill_month ?? 0}`} testId="stat-goodwill-month" />
      </div>

      <Tabs defaultValue="analytics">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews</TabsTrigger>
          <TabsTrigger value="flags" data-testid="admin-tab-flags">Flagged{flags.filter((f) => f.flag?.status === "flagged").length > 0 ? ` (${flags.filter((f) => f.flag?.status === "flagged").length})` : ""}</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages{flaggedMsgs.filter((m) => m.flag_status === "open").length > 0 ? ` (${flaggedMsgs.filter((m) => m.flag_status === "open").length})` : ""}</TabsTrigger>
          <TabsTrigger value="homereports" data-testid="admin-tab-homereports">Home Reports{homeReports.filter((r) => !r.home_report?.resolved).length > 0 ? ` (${homeReports.filter((r) => !r.home_report?.resolved).length})` : ""}</TabsTrigger>
          <TabsTrigger value="caregivers" data-testid="admin-tab-caregivers">Caregiver Links</TabsTrigger>
          <TabsTrigger value="applications" data-testid="admin-tab-apps">Applications</TabsTrigger>
          <TabsTrigger value="pros" data-testid="admin-tab-pros">Pros & Certs</TabsTrigger>
          <TabsTrigger value="military" data-testid="admin-tab-military">Military{milVerifs.length > 0 ? ` (${milVerifs.length})` : ""}</TabsTrigger>
          <TabsTrigger value="timeoff" data-testid="admin-tab-timeoff">Time Off{timeOffReqs.filter((r) => r.status === "pending").length > 0 ? ` (${timeOffReqs.filter((r) => r.status === "pending").length})` : ""}</TabsTrigger>
          <TabsTrigger value="calllist" data-testid="admin-tab-calllist">Call List</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="admin-tab-accounts">Accounts</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings{stripeKey?.needs_rotation ? " ⚠️" : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics">
          {apps.filter((a) => a.interview_status === "scheduled" && a.interview_selected).length > 0 && (
            <div className="card-metal p-5 mt-4 border-2 border-blue-300" data-testid="upcoming-interviews-card">
              <h3 className="font-heading font-black mb-3 flex items-center gap-2 text-blue-700">
                <Phone className="w-4 h-4" /> Upcoming Phone Interviews
              </h3>
              <div className="space-y-2">
                {apps.filter((a) => a.interview_status === "scheduled" && a.interview_selected)
                  .sort((x, y) => new Date(x.interview_selected) - new Date(y.interview_selected))
                  .map((a) => (
                    <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-2" data-testid={`upcoming-interview-${a.id}`}>
                      <div>
                        <p className="font-semibold text-slate-800">{a.name}</p>
                        <p className="text-xs text-slate-500">{fmtSlot(a.interview_selected)}</p>
                      </div>
                      <div className="flex gap-2">
                        <a href={`tel:${a.phone}`} className="btn-red-3d !py-1.5 !px-3 text-sm" data-testid={`interview-call-${a.id}`}><Phone className="w-3.5 h-3.5" /> {a.phone}</a>
                        <a href={`mailto:${a.email}`} className="btn-silver-3d !py-1.5 !px-3"><MessageSquare className="w-3.5 h-3.5 text-[#9b111e]" /></a>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-6 mt-4">
            <div className="card-metal p-5">
              <h3 className="font-heading font-black mb-3">Sales & Fees by Category</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={catData}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="sales" fill={RED} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="fees" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card-metal p-5">
              <h3 className="font-heading font-black mb-3">Sales by City</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={cityData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="sales" radius={[4, 4, 0, 0]}>
                    {cityData.map((_, i) => <Cell key={i} fill={RED} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          {stats && (
            <div className="grid sm:grid-cols-3 gap-4 mt-4">
              {["by_state", "by_zip"].map((key) => (
                <div key={key} className="card-metal p-4">
                  <h4 className="font-bold text-sm mb-2 capitalize">{key.replace("by_", "By ")}</h4>
                  {Object.entries(stats[key]).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm border-b border-slate-100 py-1">
                      <span>{k}</span><span className="font-bold">${v.sales.toFixed(2)}</span>
                    </div>
                  ))}
                  {Object.keys(stats[key]).length === 0 && <p className="text-xs text-slate-400">No data yet</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bookings">
          <div className="card-metal p-4 mt-4 overflow-x-auto" data-testid="admin-bookings-table">
            <div className="flex items-center gap-2 mb-3">
              <Input value={bookingSearch} data-testid="booking-search-input"
                onChange={(e) => { setBookingSearch(e.target.value); loadBookings(1, e.target.value); }}
                placeholder="Search by client, pro or service…" className="bg-white max-w-xs" />
              {bookingSearch && (
                <button onClick={() => { setBookingSearch(""); loadBookings(1, ""); }} data-testid="booking-search-clear"
                  className="text-xs font-bold text-slate-500 hover:text-[#9b111e]">Clear</button>
              )}
              <span className="text-xs text-slate-400 ml-auto">{bookingsPg.total} result{bookingsPg.total === 1 ? "" : "s"}</span>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b"><th className="p-2">Service</th><th>Client</th><th>Provider</th><th>City</th><th>Price</th><th>Fee</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100">
                    <td className="p-2">{b.service}</td><td>{b.client_name}</td><td>{b.provider_name}</td>
                    <td>{b.city || "—"}</td><td>${b.price}</td><td>${b.platform_fee}</td>
                    <td className="uppercase text-xs font-bold">
                      {b.payment_status === "cashapp_pending"
                        ? <span className="text-amber-600">Cash App — pending</span>
                        : b.payment_status}
                      {b.payment_method === "cashapp" && b.payment_status === "paid" && <span className="text-green-600"> · Cash App</span>}
                      {b.payment_method === "credit" && <span className="text-green-600"> · Credit</span>}
                      {b.is_redo && <span className="ml-1 text-blue-700 bg-blue-50 rounded px-1.5 py-0.5" data-testid={`redo-badge-${b.id}`}>FREE RE-DO</span>}
                      {b.credit_applied > 0 && <span className="text-slate-500"> · −${Number(b.credit_applied).toFixed(2)} credit</span>}
                      {b.needs_manual_payout && b.payment_status === "paid" && (
                        <span className="ml-1 inline-block text-[#9b111e] bg-red-50 rounded px-1.5 py-0.5" data-testid={`manual-payout-${b.id}`} title="Site credit was applied — settle this pro's payout manually">⚠ manual payout</span>
                      )}
                    </td>
                    <td>
                      {b.payment_status === "cashapp_pending" && (
                        <button onClick={() => confirmCash(b.id)} data-testid={`confirm-cash-${b.id}`} className="btn-red-3d !py-1.5 !px-3 text-xs">
                          <Check className="w-3.5 h-3.5" /> Confirm Cash
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {bookings.length === 0 && <p className="text-slate-400 p-4">{bookingSearch ? `No bookings match “${bookingSearch}”.` : "No bookings yet."}</p>}
            <Pager pg={bookingsPg} onPage={loadBookings} testid="bookings-pager" />
          </div>
        </TabsContent>

        <TabsContent value="reviews">
          <div className="card-metal p-5 mt-4" data-testid="review-nudges-card">
            <h3 className="font-heading font-black text-lg flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#9b111e]" /> Review Nudges — 10% Off Offers
            </h3>
            <p className="text-sm text-slate-500 mt-1 mb-3">
              These clients completed &amp; paid for bookings but never left a review. Send them a one-tap offer:
              <b> leave a fair review → 10% off their next booking</b>, or <b>refer {offerCands?.vip_threshold || 3} friends → permanent VIP 10% off</b>.
              <span className="block mt-1 text-xs text-slate-400">
                ⚙️ Auto-pilot is ON: offers go out automatically {offerCands?.auto_days || 3} days after any unreviewed paid visit,
                expire after {offerCands?.expiry_days || 14} days, and a last-chance reminder text goes out 3 days before expiry.
              </span>
            </p>
            {offerCands?.stats && offerCands.stats.total_sent > 0 && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="offer-analytics">
                <span className="text-xs font-bold text-slate-700 bg-slate-100 rounded-full px-3 py-1.5">📤 {offerCands.stats.total_sent} offer{offerCands.stats.total_sent === 1 ? "" : "s"} sent</span>
                <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1.5" data-testid="offer-conversion">
                  ✅ {offerCands.stats.converted} converted ({offerCands.stats.conversion_pct.toFixed(0)}%)
                </span>
                <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5" data-testid="offer-revenue">
                  💰 ${offerCands.stats.redeemed_revenue.toFixed(2)} booked via {offerCands.stats.redeemed_bookings} redeemed discount{offerCands.stats.redeemed_bookings === 1 ? "" : "s"}
                </span>
                <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">🎁 ${offerCands.stats.discounts_given.toFixed(2)} in discounts given</span>
                {offerCands.stats.counts.expired > 0 && (
                  <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-full px-3 py-1.5">⏳ {offerCands.stats.counts.expired} expired</span>
                )}
              </div>
            )}
            {(!offerCands || offerCands.candidates.length === 0) && (
              <p className="text-slate-400 text-sm" data-testid="no-nudge-candidates">Everyone with a completed booking has reviewed — nice! 🎉</p>
            )}
            <div className="space-y-2">
              {(offerCands?.candidates || []).map((c) => {
                const st = c.offer?.status;
                return (
                  <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`nudge-row-${c.id}`}>
                    <div>
                      <p className="font-bold text-slate-800">{c.name} <span className="font-normal text-slate-400 text-sm">· {c.email}</span></p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {c.unreviewed_count} unreviewed visit{c.unreviewed_count === 1 ? "" : "s"}
                        {c.last_service ? ` · last: ${c.last_service}` : ""} · {c.confirmed_referrals} confirmed referral{c.confirmed_referrals === 1 ? "" : "s"}
                        {c.referral_vip && <span className="ml-1 text-blue-700 font-bold">💎 VIP</span>}
                      </p>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                      {(!st || st === "expired" || st === "redeemed") && (
                        <button onClick={() => sendReviewOffer(c)} disabled={sendingOffer === c.id}
                          className="btn-red-3d !py-2 !px-4 text-sm" data-testid={`send-offer-btn-${c.id}`}>
                          {sendingOffer === c.id ? "Sending…" : st ? "Re-offer 10% off" : `Offer ${(offerCands?.offer_pct || 10).toFixed(0)}% off`}
                        </button>
                      )}
                      {st === "offered" && (
                        <span className="text-xs font-semibold text-amber-600 bg-amber-50 rounded-full px-3 py-1" data-testid={`offer-status-${c.id}`}>
                          {c.offer?.auto ? "Auto-sent" : "Offer sent"} · awaiting review
                          {c.offer?.expires_at ? ` · expires ${new Date(c.offer.expires_at).toLocaleDateString()}` : ""}
                        </span>
                      )}
                      {st === "earned" && <span className="text-xs font-semibold text-green-700 bg-green-50 rounded-full px-3 py-1" data-testid={`offer-status-${c.id}`}>Earned · 10% loaded 🎉</span>}
                      {st === "redeemed" && <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-3 py-1" data-testid={`offer-status-${c.id}`}>Redeemed ✓</span>}
                      {st === "expired" && <span className="text-xs font-semibold text-red-600 bg-red-50 rounded-full px-3 py-1" data-testid={`offer-status-${c.id}`}>Expired ⏳</span>}
                      {st === "fulfilled_vip" && <span className="text-xs font-semibold text-blue-700 bg-blue-50 rounded-full px-3 py-1" data-testid={`offer-status-${c.id}`}>Went VIP 💎</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="card-metal p-5 mt-4" data-testid="qc-results-card">
            <h3 className="font-heading font-black text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#9b111e]" /> Random Quality-Control Checks
            </h3>
            <p className="text-sm text-slate-500 mt-1 mb-3">
              Random clients who skipped a review get a 4-point checklist (knock within 5 min, dress code + foot covers,
              CARE-4U lanyard, nickname greeting). <b>Any failed item drops the pro straight to Back-Up status;
              two clean passes in a row clear a QC strike (redemption path).</b>
              {qcData ? ` ${qcData.completed} completed · ${qcData.pending} pending.` : ""}
            </p>
            {qcData?.completed > 0 && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="qc-pass-rates">
                {[["on_time", "⏰ On time"], ["dress_code", "👔 Dress code"], ["lanyard", "🪪 Lanyard"], ["nickname_greeting", "👋 Nickname"]].map(([k, label]) => (
                  qcData.pass_rates[k] !== null && (
                    <span key={k} className={`text-xs font-bold rounded-full px-3 py-1.5 border ${qcData.pass_rates[k] >= 90 ? "text-green-700 bg-green-50 border-green-200" : qcData.pass_rates[k] >= 70 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-red-700 bg-red-50 border-red-200"}`}>
                      {label}: {qcData.pass_rates[k]}% pass
                    </span>
                  )
                ))}
              </div>
            )}
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {(qcData?.checks || []).slice(0, 20).map((c) => (
                <div key={c.id} className="rounded-lg border border-slate-200 p-2.5 text-sm" data-testid={`qc-row-${c.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-800 truncate">{c.provider_name} <span className="font-normal text-slate-400">· {c.service} for {c.client_name}</span></p>
                      <p className="text-xs text-slate-400">{new Date(c.sent_at).toLocaleDateString()}</p>
                    </div>
                    {c.status === "pending" ? (
                      <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-3 py-1">Awaiting client</span>
                    ) : (
                      <div className="flex gap-1.5 items-center">
                        {[["on_time", "⏰"], ["dress_code", "👔"], ["lanyard", "🪪"], ["nickname_greeting", "👋"]].map(([k, ic]) => (
                          <span key={k} title={k} className={`text-xs font-black rounded-full w-7 h-7 flex items-center justify-center ${c.answers?.[k] ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                            {ic}
                          </span>
                        ))}
                        {c.pro_dropped_to_backup && (
                          <span className="text-[10px] font-black text-white bg-[#9b111e] rounded-full px-2 py-1">→ BACK-UP</span>
                        )}
                      </div>
                    )}
                  </div>
                  {c.note && (
                    <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5" data-testid={`qc-note-block-${c.id}`}>
                      {c.note_status === "pending_admin" ? (
                        <>
                          <p className="text-[11px] font-bold text-slate-500 mb-1">💬 Client comment — you can lightly edit it before sending:</p>
                          <textarea
                            value={qcEditNotes[c.id] !== undefined ? qcEditNotes[c.id] : c.note}
                            onChange={(e) => setQcEditNotes((n) => ({ ...n, [c.id]: e.target.value }))}
                            rows={2}
                            className="w-full text-xs text-slate-700 bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:border-[#9b111e]"
                            data-testid={`qc-note-edit-${c.id}`} />
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-1 self-center">IN LIMBO — review language</span>
                            <button onClick={() => decideQcNote(c.id, "approve")} disabled={qcNoteBusy === c.id}
                              className="text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-full px-3 py-1" data-testid={`qc-note-approve-${c.id}`}>
                              {qcNoteBusy === c.id ? "…" : qcEditNotes[c.id] !== undefined && qcEditNotes[c.id] !== c.note ? "Approve edited & send" : "Approve & send to pro"}
                            </button>
                            <button onClick={() => decideQcNote(c.id, "reject")} disabled={qcNoteBusy === c.id}
                              className="text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-full px-3 py-1" data-testid={`qc-note-reject-${c.id}`}>
                              Reject
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-slate-600 italic">💬 "{c.note}"</p>
                          {c.note_edited && c.note_original && (
                            <p className="text-[10px] text-slate-400 mt-0.5">Original: "{c.note_original}"</p>
                          )}
                          {c.note_status === "approved" && (
                            <p className="text-[11px] font-bold text-green-700 mt-1" data-testid={`qc-note-status-${c.id}`}>
                              ✓ Delivered to {c.provider_name}{c.note_edited ? " (lightly edited)" : ""}
                            </p>
                          )}
                          {c.note_status === "rejected" && <p className="text-[11px] font-bold text-red-600 mt-1" data-testid={`qc-note-status-${c.id}`}>✗ Rejected — never sent</p>}
                        </>
                      )}
                      {c.reply && (
                        <div className="mt-2 pt-2 border-t border-slate-200" data-testid={`qc-reply-block-${c.id}`}>
                          {c.reply_status === "pending_admin" ? (
                            <>
                              <p className="text-[11px] font-bold text-slate-500 mb-1">↩️ {c.provider_name}'s thank-you to the client — edit if needed:</p>
                              <textarea
                                value={qcEditNotes[`r-${c.id}`] !== undefined ? qcEditNotes[`r-${c.id}`] : c.reply}
                                onChange={(e) => setQcEditNotes((n) => ({ ...n, [`r-${c.id}`]: e.target.value }))}
                                rows={2}
                                className="w-full text-xs text-slate-700 bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:border-[#9b111e]"
                                data-testid={`qc-reply-edit-${c.id}`} />
                              <div className="flex flex-wrap gap-2 mt-2">
                                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-1 self-center">IN LIMBO — review language</span>
                                <button onClick={() => decideQcReply(c.id, "approve")} disabled={qcNoteBusy === c.id}
                                  className="text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-full px-3 py-1" data-testid={`qc-reply-approve-${c.id}`}>
                                  {qcNoteBusy === c.id ? "…" : "Approve & send to client"}
                                </button>
                                <button onClick={() => decideQcReply(c.id, "reject")} disabled={qcNoteBusy === c.id}
                                  className="text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-full px-3 py-1" data-testid={`qc-reply-reject-${c.id}`}>
                                  Reject
                                </button>
                              </div>
                            </>
                          ) : (
                            <p className="text-[11px]" data-testid={`qc-reply-admin-status-${c.id}`}>
                              <span className="text-slate-500 italic">↩️ Pro's thank-you: "{c.reply}"</span>{" "}
                              {c.reply_status === "approved" && <span className="font-bold text-green-700">✓ Delivered to {c.client_name}{c.reply_edited ? " (lightly edited)" : ""}</span>}
                              {c.reply_status === "rejected" && <span className="font-bold text-red-600">✗ Rejected — never sent</span>}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {qcData && qcData.checks.length === 0 && <p className="text-slate-400 text-sm">No QC checks sent yet — the daily sweep picks up to 3 random unreviewed paid visits.</p>}
            </div>
          </div>
          <div className="space-y-2 mt-4">
            {reviews.map((r) => (
              <div key={r.id} className="card-metal p-4" data-testid={`admin-review-${r.id}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-bold text-[#9b111e]">{r.rating}/10 — {r.provider_name}</p>
                    <p className="text-sm text-slate-600">{r.comment} <span className="text-slate-400">by {r.client_name}</span></p>
                  </div>
                  <button onClick={() => delReview(r.id)} className="btn-silver-3d !py-2 !px-3" data-testid={`admin-del-review-${r.id}`}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
                {(r.photos || []).length > 0 && (
                  <div className="flex gap-3 mt-3 flex-wrap" data-testid={`admin-review-photos-${r.id}`}>
                    {r.photos.map((p) => (
                      <div key={p.idx} className="relative">
                        {p.hidden ? (
                          <button onClick={() => previewHiddenPhoto(r.id, p.idx)}
                            className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-400 bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600 text-center px-1 hover:border-[#9b111e] hover:text-[#9b111e] transition-colors"
                            data-testid={`preview-hidden-photo-${r.id}-${p.idx}`}>
                            Hidden — click to preview
                          </button>
                        ) : (
                          <img src={`${API}/reviews/${r.id}/photo/${p.idx}`} alt="Review" loading="lazy"
                            className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                        )}
                        <button onClick={() => togglePhoto(r.id, p.idx)}
                          className={`absolute -top-2 -right-2 text-[10px] font-bold rounded-full px-2 py-0.5 border shadow-sm ${p.hidden ? "bg-green-600 text-white border-green-700" : "bg-white text-slate-700 border-slate-400 hover:text-red-600 hover:border-red-400"}`}
                          data-testid={`toggle-photo-${r.id}-${p.idx}`}>
                          {p.hidden ? "Unhide" : "Hide"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {reviews.length === 0 && <p className="text-slate-400">No reviews yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="flags">
          <div className="space-y-3 mt-4" data-testid="admin-flags-list">
            {flags.filter((f) => f.flag?.status === "flagged").length === 0 && (
              <p className="text-slate-400">No flagged reviews awaiting your decision.</p>
            )}
            {flags.map((r) => {
              const st = r.flag?.status;
              const call = r.flag?.call || {};
              return (
                <div key={r.id} className="card-metal p-4" data-testid={`admin-flag-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="font-bold text-[#9b111e]">
                        {r.rating}/10 — {r.provider_name}
                        {st === "resolved_removed" && <span className="ml-2 text-xs text-green-700 bg-green-50 rounded-full px-2 py-0.5">removed</span>}
                        {st === "resolved_upheld" && <span className="ml-2 text-xs text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">kept</span>}
                        {st === "flagged" && <span className="ml-2 text-xs text-amber-600 bg-amber-50 rounded-full px-2 py-0.5">awaiting decision</span>}
                      </p>
                      <p className="text-sm text-slate-600 mt-1"><b>Service:</b> {r.service} · <b>Client:</b> {r.client_name}</p>
                      <p className="text-sm text-slate-600 mt-1"><b>Client's review:</b> {r.comment || <span className="italic text-slate-400">(no comment)</span>}</p>
                      <p className="text-sm text-slate-800 mt-2 bg-amber-50 border border-amber-200 rounded p-2"><b>Pro's reason it's unfair:</b> {r.flag?.reason}</p>
                      {call.status === "scheduled" && (
                        <p className="text-sm text-blue-700 mt-2">📞 Client call scheduled: {new Date(call.selected).toLocaleString()}</p>
                      )}
                      {call.status === "offered" && (
                        <p className="text-sm text-slate-500 mt-2">📞 Call times offered — waiting for the client to pick.</p>
                      )}
                      {st && st !== "flagged" && r.flag?.resolution && r.flag.resolution !== "none" && (
                        <p className="text-sm text-slate-500 mt-1"><b>Resolution:</b> {r.flag.resolution}{r.flag.notes ? ` — ${r.flag.notes}` : ""}</p>
                      )}
                      {r.flag?.redo_booking_id && (
                        <p className="text-sm text-blue-700 mt-1 flex items-center gap-1" data-testid={`flag-redo-sent-${r.id}`}><Wrench className="w-3.5 h-3.5" /> Free re-do sent — client to schedule</p>
                      )}
                    </div>
                    {st === "flagged" && (
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button onClick={() => openFlagCall(r)} className="btn-silver-3d !py-2 !px-3 text-sm" data-testid={`flag-offer-call-${r.id}`}>
                          <Phone className="w-4 h-4 text-[#9b111e] inline mr-1" />Call client
                        </button>
                        {!r.flag?.redo_booking_id && (
                          <button onClick={() => sendRedo(r.id)} className="btn-silver-3d !py-2 !px-3 text-sm" data-testid={`flag-redo-${r.id}`}>
                            <Wrench className="w-4 h-4 text-blue-600 inline mr-1" />Free re-do
                          </button>
                        )}
                        <button onClick={() => { setResolveTarget(r); setResolveForm({ resolution: "none", notes: "", credit_amount: "" }); }} className="btn-red-3d !py-2 !px-3 text-sm" data-testid={`flag-resolve-${r.id}`}>
                          Resolve
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="homereports">
          {cleanReports.length > 0 && (
            <div className="card-metal p-4 mt-4 border-2 border-green-400" data-testid="admin-cleanreports">
              <h3 className="font-heading font-black text-green-700 flex items-center gap-2">✨ Clean Reports — homes verified spotless by cleaning pros</h3>
              <div className="space-y-3 mt-2">
                {cleanReports.map((r) => (
                  <div key={r.id} className="border border-slate-200 rounded-lg p-3" data-testid={`clean-report-${r.id}`}>
                    <p className="font-bold text-slate-800">
                      {r.provider_name} → {r.client_name}
                      <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${r.clean_report?.status === "approved" ? "text-green-700 bg-green-50" : "text-amber-700 bg-amber-50"}`}>
                        {r.clean_report?.status === "approved" ? "verified — score reset" : "awaiting your review"}
                      </span>
                    </p>
                    <p className="text-sm text-slate-600 mt-1"><b>Service:</b> {r.service} · reported clean 10/10 with {r.clean_report?.photo_count} photo(s)</p>
                    {r.clean_report?.note && <p className="text-sm text-slate-800 mt-1 bg-slate-50 border border-slate-200 rounded p-2"><b>Pro's note:</b> {r.clean_report.note}</p>}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {Array.from({ length: r.clean_report?.photo_count || 0 }).map((_, i) => (
                        <button key={i} onClick={() => viewCleanPhoto(r.id, i)} className="btn-metal text-xs px-3 py-1.5" data-testid={`clean-photo-${r.id}-${i}`}>View photo {i + 1}</button>
                      ))}
                      {r.clean_report?.status !== "approved" && (
                        <button onClick={() => { setScoreUser({ id: r.client_id, name: r.client_name }); setScoreForm({ score: "10", clear: true }); }}
                          className="btn-red-3d !py-1.5 !px-3 text-xs" data-testid={`clean-report-approve-${r.id}`}>
                          Reset home score & clear hazard
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-3 mt-4" data-testid="admin-homereports-list">
            {homeReports.length === 0 && <p className="text-slate-400">No home reports filed by pros.</p>}
            {homeReports.map((r) => (
              <div key={r.id} className={`card-metal p-4 ${r.home_report?.severity === "unsafe" && !r.home_report?.resolved ? "border-2 border-red-400" : ""}`} data-testid={`admin-homereport-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-bold text-[#9b111e]">
                      {r.provider_name} → {r.client_name}
                      <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${r.home_report?.severity === "unsafe" ? "text-red-700 bg-red-50" : "text-amber-700 bg-amber-50"}`}>
                        {r.home_report?.severity === "unsafe" ? "UNSAFE" : "not tidy"}
                      </span>
                      {typeof r.home_report?.score === "number" && (
                        <span className={`ml-2 text-xs font-bold rounded-full px-2 py-0.5 ${r.home_report.score < 7 ? "text-white bg-[#9b111e]" : "text-slate-700 bg-slate-100"}`} data-testid={`homereport-score-${r.id}`}>
                          {r.home_report.score}/10{r.home_report.cleared ? " · cleared" : ""}
                        </span>
                      )}
                      {r.home_report?.resolved && <span className="ml-2 text-xs text-green-700 bg-green-50 rounded-full px-2 py-0.5">reviewed</span>}
                    </p>
                    <p className="text-sm text-slate-600 mt-1"><b>Service:</b> {r.service}{r.scheduled_at ? ` · ${new Date(r.scheduled_at).toLocaleDateString()}` : ""}</p>
                    <p className="text-sm text-slate-800 mt-2 bg-slate-50 border border-slate-200 rounded p-2"><b>Pro's note:</b> {r.home_report?.issue}</p>
                    <p className="text-xs text-slate-400 mt-1">Private — the client cannot see this.</p>
                  </div>
                  {!r.home_report?.resolved && (
                    <button onClick={() => resolveHomeReport(r.id)} className="btn-silver-3d !py-2 !px-3 text-sm flex-shrink-0" data-testid={`homereport-resolve-${r.id}`}>
                      <span className="text-engraved">Mark reviewed</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="caregivers">
          <div className="card-metal p-4 mt-4 overflow-x-auto" data-testid="admin-caregivers-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b"><th className="p-2">Caregiver</th><th>Email</th><th>Patient</th><th>Patient Email</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {links.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100">
                    <td className="p-2">{l.caregiver_name}</td><td>{l.caregiver_email}</td>
                    <td>{l.patient_name}</td><td>{l.patient_email}</td>
                    <td className="uppercase text-xs font-bold">{l.status}</td>
                    <td>
                      <button onClick={() => delLink(l.id)} data-testid={`admin-del-link-${l.id}`}
                        title="Remove link" className="p-2 rounded-full text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {links.length === 0 && <p className="text-slate-400 p-4">No caregiver links yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="applications">
          <div className="space-y-3 mt-4">
            {apps.map((a) => (
              <div key={a.id} className="card-metal p-4" data-testid={`admin-app-${a.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="font-heading font-black">{a.name} <span className="text-slate-400 font-normal">· {a.email}</span></p>
                    <p className="text-sm text-slate-500">
                      {a.years_experience} yrs · {a.services?.length} services · {a.phone}
                      <span className={`ml-2 uppercase font-bold ${a.status === "interview_required" ? "text-blue-600" : a.status === "approved" ? "text-green-600" : a.status === "rejected" ? "text-red-600" : "text-amber-600"}`}>
                        {a.status === "interview_required" ? "Interview Requested" : a.status}
                      </span>
                    </p>
                    {(a.experience_types?.length > 0) && (
                      <p className="text-xs text-slate-500 mt-1">
                        Experience from: <b>{a.experience_types.join(", ")}</b>
                        {a.interview_available && <span className="ml-2 text-green-600">· open to phone interview</span>}
                      </p>
                    )}
                  </div>
                  {(a.status === "pending" || a.status === "interview_required") && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => decide(a.id, "approve")} className="btn-red-3d !py-2 !px-3" data-testid={`approve-app-${a.id}`} title="Approve"><Check className="w-4 h-4" /></button>
                      <button onClick={() => openInterview(a)} className="btn-silver-3d !py-2 !px-3" data-testid={`interview-app-${a.id}`} title="Offer interview times"><Phone className="w-4 h-4 text-blue-600" /></button>
                      <button onClick={() => decide(a.id, "reject")} className="btn-silver-3d !py-2 !px-3" data-testid={`reject-app-${a.id}`} title="Deny"><X className="w-4 h-4 text-red-600" /></button>
                    </div>
                  )}
                </div>
                {a.interview_status === "offered" && (
                  <p className="text-sm text-blue-700 mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2" data-testid={`app-interview-offered-${a.id}`}>
                    <b>Interview times offered</b> — waiting for the pro to pick: {(a.interview_offered_slots || []).map(fmtSlot).join(" · ")}
                  </p>
                )}
                {a.interview_status === "scheduled" && a.interview_selected && (
                  <p className="text-sm text-green-700 mt-2 bg-green-50 border border-green-200 rounded-lg p-2" data-testid={`app-interview-scheduled-${a.id}`}>
                    <b>📞 Interview scheduled:</b> {fmtSlot(a.interview_selected)} · call {a.phone}
                  </p>
                )}
                {a.experience_details && (
                  <p className="text-sm text-slate-700 mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={`app-experience-${a.id}`}>
                    <b>5-Year Experience:</b> {a.experience_details}
                  </p>
                )}
                <p className="text-sm text-slate-600 mt-2"><b>Why fit:</b> {a.why_fit}</p>
                {a.work_experiences?.length > 0 ? (
                  <div className="text-sm text-slate-600 mt-1" data-testid={`app-experience-list-${a.id}`}>
                    <b>Work / school / military history ({a.years_experience} yrs total):</b>
                    <div className="mt-1 space-y-0.5">
                      {a.work_experiences.map((e, i) => (
                        <div key={i} className="pl-3 border-l-2 border-slate-200">
                          <span className="font-semibold">{e.job_title}</span> · {e.location}
                          <span className="text-slate-400"> · {e.years} yr{e.years !== 1 ? "s" : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600"><b>History:</b> {a.work_history}</p>
                )}
                <p className="text-sm text-slate-600"><b>References:</b> {a.references}</p>
                {a.has_resume && (
                  <button onClick={() => viewResume(a.user_id)} className="btn-silver-3d !py-1 !px-3 text-xs mt-2" data-testid={`view-resume-${a.id}`}>
                    <FileText className="w-3.5 h-3.5 text-[#9b111e]" /><span className="ml-1">View Resume / CV</span>
                  </button>
                )}
                {a.experience_docs?.length > 0 && (
                  <div className="mt-2" data-testid={`app-exp-docs-${a.id}`}>
                    <b className="text-sm text-slate-600">Supporting documents ({a.experience_docs.length}):</b>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {a.experience_docs.map((d) => (
                        <button key={d.id} onClick={() => viewExpDoc(a.user_id, d.id)} className="btn-silver-3d !py-1 !px-2 text-xs" data-testid={`view-exp-doc-${d.id}`}>
                          <FileText className="w-3.5 h-3.5 text-[#9b111e]" /><span className="ml-1">{d.label || d.filename}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {a.services?.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid={`app-services-${a.id}`}>
                    <b className="text-sm text-slate-600">Services & rates:</b>
                    {a.services.map((s, i) => (
                      <div key={i} className="text-sm text-slate-600 pl-3 border-l-2 border-slate-200">
                        <span className="font-semibold">{s.name}</span> — ${s.price} <span className="text-slate-400">({s.category})</span>
                        {s.description && <span className="block text-xs text-slate-500">{s.description}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {a.certifications?.length > 0 && (
                  <div className="mt-2" data-testid={`app-certs-${a.id}`}>
                    <b className="text-sm text-slate-600">Certifications:</b>
                    <div className="flex flex-col gap-1.5 mt-1">
                      {a.certifications.map((c) => (
                        <div key={c.id} className="flex items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5" data-testid={`app-cert-${c.id}`}>
                          <span className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-[#9b111e]" />
                            <span className="font-semibold">{c.name}</span>
                            {c.reviewed && <span className="text-xs text-green-600">· verified ✓ (shows as a public badge)</span>}
                            {!c.has_doc && !c.reviewed && <span className="text-xs text-slate-400">· no file</span>}
                          </span>
                          {c.has_doc && (
                            <span className="flex gap-1.5">
                              <button onClick={() => viewCert(a.user_id, c.id)} className="btn-silver-3d !py-1 !px-2 text-xs" data-testid={`view-cert-${c.id}`}>View</button>
                              <button onClick={() => deleteCert(a.user_id, c.id)} className="btn-red-3d !py-1 !px-2 text-xs" data-testid={`delete-cert-${c.id}`}>Verify &amp; Delete</button>
                            </span>
                          )}
                          {!c.has_doc && !c.reviewed && (
                            <button onClick={() => verifyCert(a.user_id, c.id)} className="btn-red-3d !py-1 !px-2 text-xs" data-testid={`verify-cert-${c.id}`}>Approve</button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {apps.length === 0 && <p className="text-slate-400">No applications yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="pros">
          {(() => {
            const filteredPros = pros.filter((p) => !proSearch ||
              `${p.name || ""} ${p.email || ""}`.toLowerCase().includes(proSearch.toLowerCase()));
            return (
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-2">
              <Input value={proSearch} data-testid="pro-search-input"
                onChange={(e) => setProSearch(e.target.value)}
                placeholder="Search pros by name or email…" className="bg-white max-w-xs" />
              {proSearch && (
                <button onClick={() => setProSearch("")} data-testid="pro-search-clear"
                  className="text-xs font-bold text-slate-500 hover:text-[#9b111e]">Clear</button>
              )}
              <span className="text-xs text-slate-400 ml-auto" data-testid="pro-search-count">
                {filteredPros.length} pro{filteredPros.length === 1 ? "" : "s"}
              </span>
            </div>
            {filteredPros.length === 0 && (
              <p className="text-slate-400 p-4" data-testid="pro-search-empty">
                {proSearch ? `No pros match “${proSearch}”.` : "No pros yet."}
              </p>
            )}
            {filteredPros.map((p) => (
              <div key={p.id} className="card-metal p-5" data-testid={`admin-pro-${p.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-heading font-black text-lg">{p.name}
                      <span className="text-slate-400 font-normal text-sm"> · {p.email}</span></p>
                    <p className="text-sm text-slate-500">
                      {[p.city, p.state].filter(Boolean).join(", ") || "—"} · {p.badge} · {p.avg_rating || 0}/10
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`badge-tier ${p.approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                      {p.approved ? "Certified ✓" : "Not Certified"}
                    </span>
                    {p.suspended && (
                      <span className="badge-tier bg-red-100 text-red-700" data-testid={`suspended-badge-${p.id}`}>Suspended</span>
                    )}
                    {p.backup_status && (
                      <span className="badge-tier bg-amber-100 text-amber-800" data-testid={`backup-badge-admin-${p.id}`}>
                        Back-Up Pro · {p.negative_points || 0} strikes · {p.great_reviews_in_backup || 0}/6 great
                      </span>
                    )}
                    {p.has_id ? (
                      <button onClick={() => viewId(p)} data-testid={`view-id-${p.id}`} className="btn-silver-3d !py-2 !px-4">
                        <ShieldCheck className="w-4 h-4 text-[#9b111e]" />
                        <span className="text-engraved text-sm">View ID</span>
                      </button>
                    ) : (
                      <span className="text-xs font-semibold text-slate-400">
                        {p.id_verified ? "ID verified & deleted ✓" : "No ID uploaded"}
                      </span>
                    )}
                    <button onClick={() => toggleCert(p)} data-testid={`toggle-cert-${p.id}`}
                      className={p.approved ? "btn-silver-3d !py-2 !px-4" : "btn-red-3d !py-2 !px-4"}>
                      <ShieldCheck className="w-4 h-4" style={{ color: p.approved ? "#9b111e" : "#fff" }} />
                      <span className={p.approved ? "text-engraved text-sm" : "text-sm"}>
                        {p.approved ? "Un-certify" : "Certify"}
                      </span>
                    </button>
                    <button onClick={() => toggleSuspend(p)} data-testid={`suspend-${p.id}`}
                      className="btn-silver-3d !py-2 !px-4">
                      {p.suspended
                        ? <><RotateCcw className="w-4 h-4 text-green-600" /><span className="text-engraved text-sm">Reinstate</span></>
                        : <><Ban className="w-4 h-4 text-amber-600" /><span className="text-engraved text-sm">Suspend</span></>}
                    </button>
                    <button onClick={() => releasePro(p)} data-testid={`release-${p.id}`}
                      className="btn-silver-3d !py-2 !px-4">
                      <UserX className="w-4 h-4 text-red-600" /><span className="text-engraved text-sm">Remove</span>
                    </button>
                    {p.backup_status && (
                      <>
                        <button onClick={() => reinstatePro(p)} data-testid={`reinstate-backup-${p.id}`}
                          className="btn-red-3d !py-2 !px-4">
                          <RotateCcw className="w-4 h-4" /><span className="text-sm">Reinstate to Full</span>
                        </button>
                        <button onClick={() => openCoaching(p)} data-testid={`coach-${p.id}`}
                          className="btn-silver-3d !py-2 !px-4">
                          <Phone className="w-4 h-4 text-blue-600" /><span className="text-engraved text-sm">Coach</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-4 border-t border-slate-100 pt-3">
                  <p className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2">
                    <Award className="w-4 h-4 text-[#9b111e]" /> Certificates
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(p.certificates || []).map((c) => (
                      <span key={c.id} className="flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-full px-3 py-1 text-sm" data-testid={`cert-${c.id}`}>
                        <Award className="w-3 h-3 text-[#9b111e]" /> {c.name}
                        <button onClick={() => removeCert(p.id, c.id)} data-testid={`remove-cert-${c.id}`}>
                          <X className="w-3 h-3 text-red-600" />
                        </button>
                      </span>
                    ))}
                    {(!p.certificates || p.certificates.length === 0) && (
                      <span className="text-xs text-slate-400">No certificates yet.</span>
                    )}
                  </div>
                  <div className="flex gap-2 max-w-md">
                    <Input placeholder="e.g. Licensed Physical Therapist" className="bg-white text-sm"
                      data-testid={`cert-input-${p.id}`} value={certInput[p.id] || ""}
                      onChange={(e) => setCertInput({ ...certInput, [p.id]: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && addCert(p.id)} />
                    <button onClick={() => addCert(p.id)} data-testid={`add-cert-${p.id}`} className="btn-silver-3d !py-2 !px-3">
                      <Plus className="w-4 h-4 text-[#9b111e]" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="military">
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">
              Users who uploaded a military ID for veteran verification. View each ID once to confirm, then it is
              permanently deleted. Approving unlocks their 10% military discount and Verified Veteran ⭐ badge.
            </p>
            {milVerifs.map((u) => (
              <div key={u.id} className="card-metal p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-military-${u.id}`}>
                <div>
                  <p className="font-heading font-black text-lg">{u.name}
                    <span className="text-slate-400 font-normal text-sm"> · {u.email}</span></p>
                  <p className="text-sm text-slate-500">
                    {u.role} · {[u.city, u.state].filter(Boolean).join(", ") || "—"}
                  </p>
                </div>
                <button onClick={() => viewMilitary(u)} data-testid={`view-military-${u.id}`} className="btn-red-3d !py-2 !px-4">
                  <ShieldCheck className="w-4 h-4" /> Review Military ID
                </button>
              </div>
            ))}
            {milVerifs.length === 0 && <p className="text-slate-400">No pending military verifications.</p>}
          </div>
        </TabsContent>

        <TabsContent value="timeoff">
          <div className="space-y-6 mt-4">
            <TimeOffCalendar />

            <div>
              <h3 className="font-heading font-black text-lg mb-2">Time-Off Requests</h3>
              <p className="text-sm text-slate-500 mb-3">
                Requests over 3 consecutive days or more than 5 sick days a month need your approval before the dates are blocked.
              </p>
              <div className="space-y-3">
                {timeOffReqs.filter((r) => r.status === "pending").map((r) => (
                  <div key={r.id} className="card-metal p-5" data-testid={`timeoff-req-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-heading font-black">{r.pro_name}
                          <span className="text-slate-400 font-normal text-sm"> · {r.pro_email}</span>
                          <span className="ml-2 badge-tier bg-slate-100 text-slate-600 capitalize">{r.category}</span>
                          {r.max_consecutive > 3 && <span className="ml-1 badge-tier bg-amber-100 text-amber-700">{r.max_consecutive} in a row</span>}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">{r.dates.length} day(s): {r.dates.join(", ")}</p>
                        <p className="text-sm text-slate-500 mt-1"><b>Reason:</b> {r.reason}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {r.has_doctor_note && (
                          <button onClick={() => viewDoctorNote(r.id)} data-testid={`view-note-${r.id}`} className="btn-silver-3d !py-2 !px-3"><span className="text-engraved text-sm">Doctor's Note</span></button>
                        )}
                        <button onClick={() => decideTimeOff(r.id, true)} data-testid={`approve-timeoff-${r.id}`} className="btn-red-3d !py-2 !px-3"><Check className="w-4 h-4" /> Approve</button>
                        <button onClick={() => decideTimeOff(r.id, false)} data-testid={`reject-timeoff-${r.id}`} className="btn-silver-3d !py-2 !px-3"><X className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm">Reject</span></button>
                        <button onClick={() => releaseFromRequest(r)} data-testid={`release-timeoff-${r.id}`} className="btn-silver-3d !py-2 !px-3"><UserX className="w-4 h-4 text-red-600" /><span className="text-engraved text-sm">Release</span></button>
                      </div>
                    </div>
                  </div>
                ))}
                {timeOffReqs.filter((r) => r.status === "pending").length === 0 && (
                  <p className="text-slate-400">No pending time-off requests.</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="calllist">
          <div className="card-metal p-4 mt-4" data-testid="admin-calllist">
            <p className="text-sm text-slate-500 mb-3">Master list of all professionals — tap to call or text directly from your phone.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500 border-b"><th className="p-2">Name</th><th>Phone</th><th>Consent</th><th>Interview</th><th>Status</th><th>Contact</th></tr></thead>
                <tbody>
                  {pros.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100" data-testid={`calllist-row-${p.id}`}>
                      <td className="p-2 font-semibold">{p.name}</td>
                      <td>{p.phone ? <a href={`tel:${p.phone}`} className="text-[#9b111e] font-semibold">{p.phone}</a> : <span className="text-slate-400">—</span>}</td>
                      <td>{p.contact_consent ? <Check className="w-4 h-4 text-green-600" /> : <X className="w-4 h-4 text-slate-300" />}</td>
                      <td>{p.interview_available ? <Check className="w-4 h-4 text-green-600" /> : <X className="w-4 h-4 text-slate-300" />}</td>
                      <td className="uppercase text-xs font-bold">{p.approved ? "Certified" : "Pending"}</td>
                      <td>
                        {p.phone && (
                          <div className="flex gap-1">
                            <a href={`tel:${p.phone}`} data-testid={`call-${p.id}`} title="Call" className="p-2 rounded-full text-green-600 hover:bg-green-50"><Phone className="w-4 h-4" /></a>
                            <a href={`sms:${p.phone}`} data-testid={`text-${p.id}`} title="Text" className="p-2 rounded-full text-[#9b111e] hover:bg-red-50"><MessageSquare className="w-4 h-4" /></a>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pros.length === 0 && <p className="text-slate-400 p-4">No professionals yet.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="accounts">
          {refLeaders.length > 0 && (
            <div className="card-metal p-4 mt-4 border-2 border-blue-300" data-testid="referral-leaderboard">
              <h3 className="font-heading font-black text-slate-800">🏆 Referral Leaderboard — your biggest fans</h3>
              <p className="text-xs text-slate-500 mt-0.5">Clients with 3+ successful referrals automatically become 💎 VIPs — 10% off all their future bookings.</p>
              <div className="space-y-1.5 mt-2">
                {refLeaders.map((l, i) => (
                  <div key={l.client_id} className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-1.5" data-testid={`ref-leader-${i}`}>
                    <p className="text-sm font-bold text-slate-800">
                      {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`} {l.name}
                      <span className="font-normal text-slate-500 text-xs ml-2">{l.email}</span>
                      {l.vip && <span className="ml-2 text-[10px] font-black text-white bg-blue-600 rounded-full px-2 py-0.5">💎 VIP · 10% off</span>}
                    </p>
                    <p className="text-xs text-slate-600">{l.invited} invited · <b className="text-green-700">{l.rewarded} booked</b> · ${l.earned.toFixed(0)} credited</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="card-metal p-4 mt-4" data-testid="admin-callist-note">
            <p className="text-xs text-slate-500">Tip: On the "Call List" tab you can tap any pro's phone to call or text them directly from your mobile.</p>
          </div>
          <div className="card-metal p-5 mt-4">
            <h3 className="font-heading font-black flex items-center gap-2 mb-3">
              <UserX className="w-5 h-5 text-[#9b111e]" /> Remove Account by Email
            </h3>
            <div className="flex flex-wrap gap-2 items-center">
              <Input type="email" placeholder="user@example.com" className="bg-white max-w-xs"
                data-testid="remove-email-input" value={removeEmail}
                onChange={(e) => setRemoveEmail(e.target.value)} />
              <button onClick={() => removeUserByEmail(removeEmail.trim().toLowerCase())}
                data-testid="remove-email-btn" className="btn-red-3d !py-2 !px-4">
                Remove Account
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Permanently deletes the user and all their bookings, reviews, favorites, applications,
              caregiver links and tips. The administrator account is protected.
            </p>
          </div>

          <div className="card-metal p-5 mt-4" data-testid="bulk-credit-card">
            <h3 className="font-heading font-black flex items-center gap-2 mb-1">
              <Gift className="w-5 h-5 text-green-600" /> Bulk Site Credit
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Grant the same credit to many clients at once — great after an outage or as a promo.
              Tick the boxes next to clients below, or grant to everyone.
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              <Input type="number" min="0" step="0.01" placeholder="Amount $" className="bg-white max-w-[120px]"
                data-testid="bulk-credit-amount" value={bulkForm.amount}
                onChange={(e) => setBulkForm((f) => ({ ...f, amount: e.target.value }))} />
              <Input type="text" placeholder="Reason (optional)" className="bg-white max-w-xs"
                data-testid="bulk-credit-reason" value={bulkForm.reason}
                onChange={(e) => setBulkForm((f) => ({ ...f, reason: e.target.value }))} />
              <button onClick={() => submitBulk(false)} className="btn-silver-3d !py-2 !px-4" data-testid="bulk-credit-selected-btn">
                <span className="text-engraved">Grant to {selectedClients.length} selected</span>
              </button>
              <button onClick={() => submitBulk(true)} className="btn-red-3d !py-2 !px-4" data-testid="bulk-credit-all-btn">
                Grant to ALL clients
              </button>
            </div>
          </div>

          <div className="card-metal p-4 mt-4 overflow-x-auto" data-testid="admin-users-table">
            <div className="flex items-center gap-2 mb-3">
              <Input value={userSearch} data-testid="user-search-input"
                onChange={(e) => { setUserSearch(e.target.value); loadUsers(1, e.target.value); }}
                placeholder="Search by name or email…" className="bg-white max-w-xs" />
              {userSearch && (
                <button onClick={() => { setUserSearch(""); loadUsers(1, ""); }} data-testid="user-search-clear"
                  className="text-xs font-bold text-slate-500 hover:text-[#9b111e]">Clear</button>
              )}
              <span className="text-xs text-slate-400 ml-auto">{usersPg.total} account{usersPg.total === 1 ? "" : "s"}</span>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b"><th className="p-2 w-8"></th><th className="p-2">Name</th><th>Email</th><th>Role</th><th>Location</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100" data-testid={`admin-user-${u.id}`}>
                    <td className="p-2">
                      {u.role === "client" && (
                        <input type="checkbox" className="accent-green-600 w-4 h-4"
                          checked={selectedClients.includes(u.id)}
                          onChange={() => toggleClient(u.id)}
                          data-testid={`bulk-select-${u.id}`} />
                      )}
                    </td>
                    <td className="p-2">{u.name}</td>
                    <td>{u.email}</td>
                    <td className="uppercase text-xs font-bold text-[#9b111e]">{u.role}</td>
                    <td>{[u.city, u.state].filter(Boolean).join(", ") || "—"}</td>
                    <td className="text-xs">
                      {u.role === "professional" ? (u.approved ? "Vetted" : "Pending") : "—"}
                      {u.paused && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-bold text-white bg-slate-600 rounded-full px-2 py-0.5" data-testid={`paused-badge-${u.id}`}><Ban className="w-3 h-3" /> Paused</span>
                      )}
                      {u.home_report_count >= 2 && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-bold text-red-700 bg-red-100 rounded-full px-2 py-0.5" data-testid={`repeat-offender-${u.id}`} title="Multiple home reports — decide whether to keep serving">
                          <AlertTriangle className="w-3 h-3" /> {u.home_report_count} home reports
                        </span>
                      )}
                      {u.home_report_count === 1 && (
                        <span className="ml-1 text-[10px] font-semibold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5" data-testid={`home-report-count-${u.id}`}>1 home report</span>
                      )}
                      {u.cleaning_required && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-bold text-[#9b111e] bg-red-100 rounded-full px-2 py-0.5" data-testid={`cleaning-required-badge-${u.id}`} title="Must book & complete a Care-4U cleaning before other services">
                          <Sparkles className="w-3 h-3" /> Cleaning required
                        </span>
                      )}
                      {u.hazardous && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-black text-white bg-[#9b111e] rounded-full px-2 py-0.5" data-testid={`hazardous-badge-${u.id}`} title="2+ home scores under 7 — booking paused until a paid deep cleaning is verified with photos">
                          <img src="/hazard-home.png" alt="" className="w-3.5 h-3.5 rounded-full bg-white" /> HAZARDOUS HOME
                        </span>
                      )}
                      {u.terminated && (
                        <span className="ml-1 text-[10px] font-black text-white bg-black rounded-full px-2 py-0.5" data-testid={`terminated-badge-${u.id}`} title={`Services terminated permanently${u.terminated_at ? ` on ${new Date(u.terminated_at).toLocaleDateString()}` : ""}`}>⛔ TERMINATED</span>
                      )}
                      {!u.terminated && u.termination_review && (
                        <span className="ml-1 text-[10px] font-black text-white bg-black rounded-full px-2 py-0.5" data-testid={`termination-review-badge-${u.id}`} title="Low report AFTER coaching — services may be terminated permanently">⚠ TERMINATION REVIEW</span>
                      )}
                      {!u.hazardous && u.hazard_final_warning && (
                        <span className="ml-1 text-[10px] font-semibold text-amber-800 bg-amber-100 rounded-full px-2 py-0.5" data-testid={`final-warning-badge-${u.id}`} title="Coached after a professional cleaning reset — one more low report may mean permanent termination">final warning on record</span>
                      )}
                      {typeof u.home_score === "number" && (
                        <span className="ml-1 text-[10px] font-semibold text-slate-700 bg-slate-100 rounded-full px-2 py-0.5" data-testid={`home-score-badge-${u.id}`}>home {u.home_score}/10</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {u.role === "client" && (
                          <button onClick={() => sendFeedbackEmail(u)} data-testid={`feedback-user-${u.id}`}
                            title="Email a feedback request" className="p-2 rounded-full text-slate-500 hover:text-[#9b111e] hover:bg-red-50 transition-colors">
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        )}
                        {u.role === "client" && (
                          <button onClick={() => { setCreditUser(u); setCreditForm({ amount: "", reason: "" }); }} data-testid={`grant-credit-user-${u.id}`}
                            title="Grant site credit" className="p-2 rounded-full text-green-600 hover:bg-green-50 transition-colors flex items-center gap-1">
                            <Gift className="w-4 h-4" />
                            {u.site_credit > 0 && <span className="text-xs font-bold" data-testid={`user-credit-${u.id}`}>${Number(u.site_credit).toFixed(2)}</span>}
                          </button>
                        )}
                        {u.role === "client" && !u.terminated && u.termination_review && (
                          <button onClick={() => terminateClient(u)} data-testid={`terminate-user-${u.id}`}
                            title="Terminate services PERMANENTLY — cancels all upcoming bookings and recurring schedules"
                            className="text-[10px] font-black text-white bg-black hover:bg-[#9b111e] rounded-full px-3 py-1.5 transition-colors">
                            ⛔ TERMINATE
                          </button>
                        )}
                        {u.role === "client" && u.terminated && (
                          <button onClick={() => reinstateClient(u)} data-testid={`reinstate-user-${u.id}`}
                            title="Reinstate this account on a final-chance basis"
                            className="text-[10px] font-bold text-green-700 bg-green-50 hover:bg-green-100 border border-green-300 rounded-full px-3 py-1.5 transition-colors">
                            Reinstate
                          </button>
                        )}
                        {u.role === "client" && (u.hazardous || u.cleaning_required) && (
                          <button onClick={() => openNudge(u)} data-testid={`nudge-user-${u.id}`}
                            title="Preview / send the cleaning reminder email" className="p-2 rounded-full text-blue-600 hover:bg-blue-50 transition-colors">
                            <Mail className="w-4 h-4" />
                          </button>
                        )}
                        {u.role === "client" && (u.hazardous || u.low_score_count > 0 || typeof u.home_score === "number") && (
                          <button onClick={() => { setScoreUser(u); setScoreForm({ score: "10", clear: u.hazardous || u.low_score_count > 0 }); }} data-testid={`edit-home-score-${u.id}`}
                            title="Edit home score / clear HAZARDOUS HOME status" className="p-1.5 rounded-full hover:bg-red-50 transition-colors">
                            <img src="/hazard-home.png" alt="Edit home score" className="w-5 h-5" />
                          </button>
                        )}
                        {u.role === "client" && u.cleaning_required && (
                          <button onClick={() => clearCleaning(u)} data-testid={`clear-cleaning-${u.id}`}
                            title="Clear the mandatory cleaning requirement" className="p-2 rounded-full text-[#9b111e] hover:bg-red-50 transition-colors">
                            <Sparkles className="w-4 h-4" />
                          </button>
                        )}
                        {u.role === "client" && (
                          <button onClick={() => togglePause(u)} data-testid={`pause-user-${u.id}`}
                            title={u.paused ? "Resume booking" : "Pause from booking"}
                            className={`p-2 rounded-full transition-colors ${u.paused ? "text-green-600 hover:bg-green-50" : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"}`}>
                            {u.paused ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                          </button>
                        )}
                        <button onClick={() => renameUser(u)} data-testid={`rename-user-${u.id}`}
                          title="Rename account" className="p-2 rounded-full text-slate-500 hover:text-[#9b111e] hover:bg-red-50 transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        {u.role !== "admin" && (
                          <button onClick={() => removeUserByEmail(u.email)} data-testid={`remove-user-${u.id}`}
                            title="Remove account" className="p-2 rounded-full text-red-600 hover:bg-red-50 transition-colors">
                            <UserX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && <p className="text-slate-400 p-4">{userSearch ? `No accounts match “${userSearch}”.` : "No accounts."}</p>}
            <Pager pg={usersPg} onPage={loadUsers} testid="users-pager" />
          </div>

          <Dialog open={!!scoreUser} onOpenChange={(o) => !o && setScoreUser(null)}>
            <DialogContent data-testid="home-score-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <img src="/hazard-home.png" alt="" className="w-7 h-7" /> Edit home score — {scoreUser?.name}
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-600">
                Set the client's home score after their professional cleaning. Clearing the hazard unblocks booking, stamps a <b>permanent record</b> on their profile, and sends the coaching email (their place NEEDS TO STAY clean — one more low report and services may be terminated PERMANENTLY).
              </p>
              <label className="text-sm font-semibold text-slate-700">New home score (1–10)</label>
              <Input type="number" min="1" max="10" value={scoreForm.score}
                onChange={(e) => setScoreForm((f) => ({ ...f, score: e.target.value }))}
                data-testid="home-score-input" />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="accent-[#9b111e] w-4 h-4" checked={scoreForm.clear}
                  onChange={(e) => setScoreForm((f) => ({ ...f, clear: e.target.checked }))}
                  data-testid="home-score-clear-checkbox" />
                Clear HAZARDOUS HOME status, stamp the permanent record & send the coaching email
              </label>
              <button onClick={submitHomeScore} className="btn-red-3d w-full !py-2.5" data-testid="home-score-save-btn">
                Save home score
              </button>
            </DialogContent>
          </Dialog>

          <Dialog open={!!nudgeUser} onOpenChange={(o) => !o && setNudgeUser(null)}>
            <DialogContent className="max-w-xl" data-testid="nudge-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#9b111e]" /> Cleaning reminder — {nudgeUser?.name}
                </DialogTitle>
              </DialogHeader>
              {nudgePreview ? (
                <>
                  <p className="text-xs text-slate-500" data-testid="nudge-meta">
                    To: <b>{nudgePreview.to}</b> · {nudgePreview.last_sent ? `last sent ${new Date(nudgePreview.last_sent).toLocaleString()}` : "never sent"} · auto-nudges daily at 10am ET (max every 3 days)
                  </p>
                  <p className="text-sm font-semibold text-slate-800">Subject: {nudgePreview.subject}</p>
                  <div className="border border-slate-200 rounded-lg p-3 max-h-80 overflow-y-auto bg-white"
                    data-testid="nudge-preview-html" dangerouslySetInnerHTML={{ __html: nudgePreview.html }} />
                  <button onClick={sendNudge} className="btn-red-3d w-full !py-2.5" data-testid="nudge-send-btn">
                    Send reminder now
                  </button>
                </>
              ) : (
                <p className="text-slate-400 text-sm">Loading preview…</p>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="settings">
          <div className="card-metal p-6 mt-4 max-w-2xl" data-testid="stripe-settings-card">
            <h3 className="font-heading font-black text-lg flex items-center gap-2 mb-1">
              <KeyRound className="w-5 h-5 text-[#9b111e]" /> Stripe API Secret Key
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Paste a new Stripe secret key any time to rotate it instantly — payments and payouts switch over with no restart.
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="badge-tier bg-slate-100 text-slate-700 font-mono" data-testid="stripe-key-masked">
                {stripeKey?.masked_key || "—"}
              </span>
              {stripeKey?.mode && (
                <span className={`badge-tier ${stripeKey.mode === "live" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid="stripe-key-mode">
                  {stripeKey.mode.toUpperCase()} MODE
                </span>
              )}
              {stripeKey?.days_since_update != null && (
                <span className={`text-sm font-semibold ${stripeKey.needs_rotation ? "text-amber-600" : "text-slate-500"}`} data-testid="stripe-key-age">
                  Updated {stripeKey.days_since_update === 0 ? "today" : `${stripeKey.days_since_update} day(s) ago`}
                </span>
              )}
            </div>

            {stripeKey?.needs_rotation && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 mb-4 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>This key is over {stripeKey.rotation_days} days old. Rotate it now to keep transactions secure.</span>
              </div>
            )}

            <label className="text-sm font-semibold text-slate-700">New Stripe secret key</label>
            <div className="flex flex-wrap gap-3 mt-1">
              <Input type="password" autoComplete="off" placeholder="sk_live_..." className="bg-white flex-1 min-w-[260px] font-mono"
                data-testid="stripe-key-input" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
              <button onClick={saveStripeKey} disabled={savingKey} className="btn-red-3d" data-testid="stripe-key-save-btn">
                {savingKey ? "Updating…" : "Update Key"}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              🔒 Keys are stored securely server-side and never shown in full. Find or regenerate yours at
              dashboard.stripe.com → Developers → API keys. Rotate at least every {stripeKey?.rotation_days || 30} days.
            </p>
          </div>

          <div className="card-metal p-6 mt-4 max-w-2xl" data-testid="new-hire-doc-card">
            <h3 className="font-heading font-black text-lg flex items-center gap-2 mb-1">
              <FileText className="w-5 h-5 text-[#9b111e]" /> New Hire Welcome PDF
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Every newly approved pro automatically receives a <b>Welcome New Hire</b> email outlining company standards
              (dress, speech &amp; service). Upload your official PDF here and it will be attached to that email.
            </p>
            {hireDoc?.uploaded ? (
              <div className="flex items-center gap-2 mb-3 text-sm" data-testid="new-hire-doc-status">
                <Check className="w-4 h-4 text-green-600" />
                <span className="font-semibold text-green-700">{hireDoc.filename}</span>
                <span className="text-slate-400">· {hireDoc.size >= 1024 ? `${(hireDoc.size / 1024).toFixed(1)} KB` : "<1 KB"} · uploaded {hireDoc.uploaded_at ? new Date(hireDoc.uploaded_at).toLocaleDateString() : ""}</span>
                <button onClick={previewHireDoc} className="text-xs font-semibold text-[#9b111e] underline" data-testid="new-hire-doc-preview-btn">Preview</button>
              </div>
            ) : (
              <p className="text-sm text-amber-600 font-semibold mb-3" data-testid="new-hire-doc-status">
                No PDF uploaded yet — the standards email still goes out, just without the attachment.
              </p>
            )}
            <label className="btn-silver-3d !py-2 !px-4 inline-flex items-center gap-2 cursor-pointer" data-testid="new-hire-doc-upload-btn">
              <FileText className="w-4 h-4 text-[#9b111e]" />
              <span className="text-engraved text-sm">{uploadingHireDoc ? "Uploading…" : hireDoc?.uploaded ? "Replace PDF" : "Upload PDF"}</span>
              <input type="file" accept="application/pdf" className="hidden" data-testid="new-hire-doc-input"
                onChange={(e) => { uploadHireDoc(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>

          <div className="card-metal p-6 mt-4 max-w-2xl" data-testid="standards-acks-card">
            <h3 className="font-heading font-black text-lg flex items-center gap-2 mb-1">
              <Check className="w-5 h-5 text-[#9b111e]" /> Standards Signatures
              {acks && (
                <span className="text-xs font-bold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1 ml-1" data-testid="acks-summary">
                  {acks.signed}/{acks.total} signed
                </span>
              )}
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Approved pros must agree to the Company Standards before clients can book them. Nudge anyone dragging their feet.
            </p>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {(acks?.pros || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-2.5" data-testid={`ack-row-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-800 text-sm truncate">{p.name} <span className="font-normal text-slate-400">· {p.email}</span></p>
                    {p.signed ? (
                      <p className="text-xs text-green-700 font-semibold" data-testid={`ack-status-${p.id}`}>
                        ✓ Signed {p.grandfathered ? "(grandfathered)" : p.signed_at ? new Date(p.signed_at).toLocaleDateString() : ""}
                      </p>
                    ) : (
                      <p className="text-xs text-red-600 font-semibold" data-testid={`ack-status-${p.id}`}>
                        ✗ Not signed — unbookable{p.nudged_at ? ` · nudged ${new Date(p.nudged_at).toLocaleDateString()}` : ""}
                      </p>
                    )}
                  </div>
                  {!p.signed && (
                    <button onClick={() => nudgePro(p)} disabled={nudgingPro === p.id}
                      className="btn-red-3d !py-1.5 !px-3 text-xs flex-shrink-0" data-testid={`nudge-pro-btn-${p.id}`}>
                      {nudgingPro === p.id ? "Sending…" : "Nudge"}
                    </button>
                  )}
                </div>
              ))}
              {acks && acks.pros.length === 0 && <p className="text-slate-400 text-sm">No approved pros yet.</p>}
            </div>
          </div>

          <div className="card-metal p-6 mt-4 max-w-2xl" data-testid="weekly-digest-card">
            <h3 className="font-heading font-black text-lg flex items-center gap-2 mb-1">
              <TrendingUp className="w-5 h-5 text-[#9b111e]" /> Weekly Digest
            </h3>
            <p className="text-sm text-slate-500 mb-3">
              Every Monday at 8 AM you'll get an email summary: bookings, revenue, fees, new reviews, offers converted
              and pros yet to sign. Want a copy right now?
            </p>
            <button onClick={sendDigest} disabled={sendingDigest} className="btn-red-3d !py-2 !px-4 text-sm" data-testid="send-digest-btn">
              {sendingDigest ? "Sending…" : "Email me this week's digest now"}
            </button>
          </div>
        </TabsContent>
        <TabsContent value="messages">
          {(() => {
            const offenders = Object.values(flaggedMsgs.reduce((acc, m) => {
              const o = acc[m.sender_id] || (acc[m.sender_id] = { id: m.sender_id, name: m.sender_name, role: m.sender_role, suspended: m.sender_suspended, total: 0, open: 0 });
              o.total += 1; if (m.flag_status === "open") o.open += 1; o.suspended = m.sender_suspended;
              return acc;
            }, {})).sort((a, b) => b.total - a.total);
            const countMap = Object.fromEntries(offenders.map((o) => [o.id, o.total]));
            return (
              <>
                {offenders.length > 0 && (
                  <div className="card-metal p-4 mt-4" data-testid="offender-radar">
                    <p className="font-heading font-black text-sm text-slate-800">🚨 Repeat Offender Radar</p>
                    <p className="text-xs text-slate-500 mb-2">Total flags per user — repeat names deserve a closer look.</p>
                    <div className="flex flex-wrap gap-2">
                      {offenders.map((o) => (
                        <span key={o.id} data-testid={`offender-${o.id}`}
                          className={`text-xs font-bold rounded-full px-3 py-1.5 border ${o.total >= 2 ? "bg-red-50 border-red-300 text-red-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                          {o.name} <span className="font-normal text-[10px] uppercase">({o.role === "professional" ? "pro" : "client"})</span> · {o.total} flag{o.total === 1 ? "" : "s"}
                          {o.open > 0 && <span className="ml-1 text-red-600">({o.open} open)</span>}
                          {o.suspended && <span className="ml-1">⛔ suspended</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="space-y-3 mt-4" data-testid="admin-flagged-messages">
                  <p className="text-sm text-slate-500">Direct messages between clients and pros that a user reported as inappropriate. You can edit the text, remove the message for both users, or dismiss the flag.</p>
                  {flaggedMsgs.length === 0 && <p className="text-slate-500">No flagged messages. 🎉</p>}
                  {flaggedMsgs.map((m) => (
              <div key={m.id} className={`card-metal p-4 ${m.flag_status === "open" ? "border-l-4 border-red-500" : "opacity-80"}`} data-testid={`flagged-msg-${m.id}`}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-slate-500">
                      <b>{m.sender_name}</b>{countMap[m.sender_id] >= 2 && <span className="ml-1 text-red-600 font-bold" data-testid={`repeat-badge-${m.id}`}>({countMap[m.sender_id]}× flagged)</span>} → <b>{m.recipient_name}</b> · {new Date(m.created_at).toLocaleString()} ·{" "}
                      <span className={`font-bold uppercase ${m.flag_status === "open" ? "text-red-600" : m.flag_status === "dismissed" ? "text-slate-400" : "text-green-600"}`}>{m.flag_status}</span>
                      {m.deleted && <span className="ml-2 text-red-600 font-bold">DELETED</span>}
                    </p>
                    {m.flag_reason && <p className="text-xs text-amber-700 mt-0.5">Reported by {m.flagged_by_name}: “{m.flag_reason}”</p>}
                    {m.photo_url && (
                      <a href={`${api.defaults.baseURL}${m.photo_url}`} target="_blank" rel="noreferrer">
                        <img src={`${api.defaults.baseURL}${m.photo_url}`} alt="flagged attachment" loading="lazy"
                          className="mt-2 rounded-lg max-h-32 w-auto border border-red-200" data-testid={`flagged-msg-photo-${m.id}`} />
                      </a>
                    )}
                    {editMsgId === m.id ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Input value={editMsgText} onChange={(e) => setEditMsgText(e.target.value)} className="flex-1 min-w-[240px]" data-testid={`edit-msg-input-${m.id}`} />
                        <button onClick={() => saveMsgEdit(m.id)} className="bg-[#9b111e] text-white text-xs font-bold rounded-lg px-3 py-1.5" data-testid={`save-msg-edit-${m.id}`}>Save</button>
                        <button onClick={() => setEditMsgId(null)} className="text-xs text-slate-500 px-2">Cancel</button>
                      </div>
                    ) : (
                      <p className={`mt-1 text-sm ${m.deleted ? "line-through text-slate-400" : "text-slate-800"}`}>{m.text}</p>
                    )}
                    {m.admin_edited && m.original_text && <p className="text-[11px] text-blue-600 mt-0.5">edited by admin · original: “{m.original_text}”</p>}
                  </div>
                  <div className="flex flex-wrap gap-2 items-start">
                    <button onClick={() => openMsgThread(m)} className="text-xs font-bold border border-slate-300 rounded-lg px-3 py-1.5 hover:border-[#9b111e]" data-testid={`view-msg-thread-${m.id}`}>View thread</button>
                    {!m.deleted && editMsgId !== m.id && (
                      <button onClick={() => { setEditMsgId(m.id); setEditMsgText(m.text); }} className="text-xs font-bold border border-slate-300 rounded-lg px-3 py-1.5 hover:border-[#9b111e]" data-testid={`edit-msg-btn-${m.id}`}>Edit</button>
                    )}
                    {!m.deleted && (
                      <button onClick={() => delMsg(m.id)} className="text-xs font-bold text-white bg-red-600 rounded-lg px-3 py-1.5" data-testid={`delete-msg-btn-${m.id}`}>Delete</button>
                    )}
                    {m.flag_status === "open" && (
                      <button onClick={() => dismissMsgFlag(m.id)} className="text-xs font-bold border border-slate-300 rounded-lg px-3 py-1.5 hover:border-green-600" data-testid={`dismiss-msg-flag-${m.id}`}>Dismiss flag</button>
                    )}
                    <button onClick={() => suspendSender(m)} data-testid={`suspend-sender-${m.id}`}
                      className={`text-xs font-bold rounded-lg px-3 py-1.5 ${m.sender_suspended ? "border border-green-600 text-green-700" : "border border-red-600 text-red-700 hover:bg-red-50"}`}>
                      {m.sender_suspended ? "Reinstate sender" : "Suspend sender"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
                </div>
              </>
            );
          })()}
          <Dialog open={!!msgThread} onOpenChange={(o) => !o && setMsgThread(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Full conversation</DialogTitle>
                <DialogDescription>{msgThread?.flagged?.sender_name} ↔ {msgThread?.flagged?.recipient_name}</DialogDescription>
              </DialogHeader>
              <div className="max-h-[50vh] overflow-y-auto space-y-2" data-testid="admin-msg-thread">
                {msgThread?.msgs?.map((tm) => (
                  <div key={tm.id} className={`rounded-lg p-2 text-sm ${tm.id === msgThread.flagged.id ? "bg-red-50 border border-red-300" : "bg-slate-50"} ${tm.deleted ? "opacity-60" : ""}`}>
                    <p className="text-[11px] text-slate-500 font-bold">{tm.sender_name} · {new Date(tm.created_at).toLocaleString()}</p>
                    {tm.photo_url && (
                      <a href={`${api.defaults.baseURL}${tm.photo_url}`} target="_blank" rel="noreferrer">
                        <img src={`${api.defaults.baseURL}${tm.photo_url}`} alt="attachment" loading="lazy"
                          className="my-1 rounded-lg max-h-28 w-auto border border-slate-200" />
                      </a>
                    )}
                    <p className={tm.deleted ? "line-through text-slate-400" : ""}>{tm.text}</p>
                    {tm.flagged && <p className="text-[10px] text-red-600 font-bold">🚩 flagged ({tm.flag_status})</p>}
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>

      <Dialog open={!!interviewApp} onOpenChange={(o) => { if (!o) setInterviewApp(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Offer interview times — {interviewApp?.name}</DialogTitle>
            <DialogDescription>Propose a few times. The pro will pick one and you'll both be notified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {interviewSlots.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input type="datetime-local" className="bg-white" data-testid={`interview-slot-input-${i}`}
                  value={s} onChange={(e) => setInterviewSlots((prev) => prev.map((v, j) => j === i ? e.target.value : v))} />
                {interviewSlots.length > 1 && (
                  <button onClick={() => setInterviewSlots((prev) => prev.filter((_, j) => j !== i))} data-testid={`remove-slot-${i}`}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                )}
              </div>
            ))}
            {interviewSlots.length < 5 && (
              <button onClick={() => setInterviewSlots((prev) => [...prev, ""])} className="btn-silver-3d !py-2 !px-3" data-testid="add-slot-btn">
                <Plus className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm ml-1">Add another time</span>
              </button>
            )}
            <button onClick={submitInterview} className="btn-red-3d w-full" data-testid="send-interview-btn">Send Times to Pro</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!coachPro} onOpenChange={(o) => { if (!o) setCoachPro(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Schedule coaching call — {coachPro?.name}</DialogTitle>
            <DialogDescription>Offer a few times for a coaching call to help this Back-Up Pro get reinstated.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {coachSlots.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input type="datetime-local" className="bg-white" data-testid={`coach-slot-input-${i}`}
                  value={s} onChange={(e) => setCoachSlots((prev) => prev.map((v, j) => j === i ? e.target.value : v))} />
                {coachSlots.length > 1 && (
                  <button onClick={() => setCoachSlots((prev) => prev.filter((_, j) => j !== i))}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                )}
              </div>
            ))}
            {coachSlots.length < 5 && (
              <button onClick={() => setCoachSlots((prev) => [...prev, ""])} className="btn-silver-3d !py-2 !px-3" data-testid="add-coach-slot-btn">
                <Plus className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm ml-1">Add another time</span>
              </button>
            )}
            <button onClick={submitCoaching} className="btn-red-3d w-full" data-testid="send-coaching-btn">Send Coaching Times</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!flagCall} onOpenChange={(o) => { if (!o) setFlagCall(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Call {flagCall?.client_name} about the flagged review</DialogTitle>
            <DialogDescription>Offer a few times. The client picks one and you'll both be notified — a chance to make it right (redo, credit, or just listen).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {flagCallSlots.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input type="datetime-local" className="bg-white" data-testid={`flagcall-slot-input-${i}`}
                  value={s} onChange={(e) => setFlagCallSlots((prev) => prev.map((v, j) => j === i ? e.target.value : v))} />
                {flagCallSlots.length > 1 && (
                  <button onClick={() => setFlagCallSlots((prev) => prev.filter((_, j) => j !== i))}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                )}
              </div>
            ))}
            {flagCallSlots.length < 5 && (
              <button onClick={() => setFlagCallSlots((prev) => [...prev, ""])} className="btn-silver-3d !py-2 !px-3" data-testid="add-flagcall-slot-btn">
                <Plus className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm ml-1">Add another time</span>
              </button>
            )}
            <button onClick={submitFlagCall} className="btn-red-3d w-full" data-testid="send-flagcall-btn">Send Call Times to Client</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolveTarget} onOpenChange={(o) => { if (!o) setResolveTarget(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Resolve flagged review — {resolveTarget?.provider_name}</DialogTitle>
            <DialogDescription>Remove the review (reverses the pro's strike) or keep it. Log how you made it right with the client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-amber-50 border border-amber-200 rounded p-2">
              <p><b>{resolveTarget?.rating}/10</b> · {resolveTarget?.service} · by {resolveTarget?.client_name}</p>
              <p className="mt-1"><b>Pro's reason:</b> {resolveTarget?.flag?.reason}</p>
            </div>
            <div>
              <label className="text-sm font-semibold">How was it resolved with the client?</label>
              <select
                className="mt-1 w-full border border-slate-300 rounded-md p-2 text-sm bg-white"
                value={resolveForm.resolution}
                onChange={(e) => setResolveForm((f) => ({ ...f, resolution: e.target.value }))}
                data-testid="resolve-resolution-select">
                <option value="none">Not specified</option>
                <option value="rectify">Talked it through / rectified</option>
                <option value="redo">Sending the pro to redo the service</option>
                <option value="credit">Site credit for future bookings</option>
              </select>
            </div>
            {resolveForm.resolution === "credit" && (
              <div data-testid="resolve-credit-amount-wrap">
                <label className="text-sm font-semibold">Credit amount ($)</label>
                <Input type="number" min="0" step="0.01" className="mt-1 bg-white"
                  value={resolveForm.credit_amount}
                  onChange={(e) => setResolveForm((f) => ({ ...f, credit_amount: e.target.value }))}
                  placeholder="e.g. 20"
                  data-testid="resolve-credit-amount" />
                <p className="text-xs text-slate-400 mt-1">Added to the client's wallet, applied automatically on their next booking.</p>
              </div>
            )}
            <div>
              <label className="text-sm font-semibold">Notes (optional)</label>
              <textarea
                className="mt-1 w-full border border-slate-300 rounded-md p-2 text-sm bg-white"
                rows={3}
                value={resolveForm.notes}
                onChange={(e) => setResolveForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="What did the client say? What did you agree to?"
                data-testid="resolve-notes-input" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => submitResolve("remove")} className="btn-red-3d flex-1" data-testid="resolve-remove-btn">Remove review (reverse strike)</button>
              <button onClick={() => submitResolve("uphold")} className="btn-silver-3d flex-1" data-testid="resolve-uphold-btn"><span className="text-engraved">Keep review</span></button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!creditUser} onOpenChange={(o) => { if (!o) setCreditUser(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Grant site credit — {creditUser?.name}</DialogTitle>
            <DialogDescription>
              Adds credit to this client's wallet. It applies automatically toward their next booking.
              {creditUser?.site_credit > 0 ? ` Current balance: $${Number(creditUser.site_credit).toFixed(2)}.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-semibold">Credit amount ($)</label>
              <Input type="number" min="0" step="0.01" className="mt-1 bg-white"
                value={creditForm.amount}
                onChange={(e) => setCreditForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="e.g. 25" data-testid="grant-credit-amount" />
            </div>
            <div>
              <label className="text-sm font-semibold">Reason (optional)</label>
              <Input type="text" className="mt-1 bg-white"
                value={creditForm.reason}
                onChange={(e) => setCreditForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Sorry about the mix-up on your last booking" data-testid="grant-credit-reason" />
            </div>
            <button onClick={submitGrantCredit} className="btn-red-3d w-full" data-testid="grant-credit-submit">Grant Credit</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!idViewer} onOpenChange={(o) => { if (!o) { if (idViewer?.url) URL.revokeObjectURL(idViewer.url); setIdViewer(null); } }}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">ID — {idViewer?.name}</DialogTitle>
            <DialogDescription>Cross-reference this ID, then permanently delete it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              🔒 Cross-reference this ID against the certifications held, then permanently delete it.
              This document is never stored on the website and should be removed once verified.
            </p>
            {idViewer?.url && (
              <img src={idViewer.url} alt="Applicant ID" className="w-full rounded-lg border border-slate-200"
                data-testid="id-viewer-image" />
            )}
            <button onClick={deleteId} data-testid="delete-id-btn" className="btn-red-3d w-full">
              <Trash2 className="w-4 h-4" /> Verify &amp; Permanently Delete ID
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!milViewer} onOpenChange={(o) => { if (!o) { if (milViewer?.url) URL.revokeObjectURL(milViewer.url); setMilViewer(null); } }}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Military ID — {milViewer?.name}</DialogTitle>
            <DialogDescription>Confirm military/veteran status, then this ID is permanently deleted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              🔒 Verify this military ID belongs to {milViewer?.name}. Whichever choice you make below,
              the document is <b>permanently deleted</b> from secure storage and is never shared.
            </p>
            {milViewer?.url && (
              <img src={milViewer.url} alt="Military ID" className="w-full rounded-lg border border-slate-200"
                data-testid="military-viewer-image" />
            )}
            <div className="flex gap-3">
              <button onClick={() => verifyMilitary(true)} data-testid="approve-military-btn" className="btn-red-3d flex-1">
                <Check className="w-4 h-4" /> Approve — Verified Veteran ⭐
              </button>
              <button onClick={() => verifyMilitary(false)} data-testid="reject-military-btn" className="btn-silver-3d flex-1">
                <X className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-sm">Reject &amp; Delete</span>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
